<template>
  <div class="overview-summary">
    <!-- KPI 카드 섹션 -->
    <div class="kpi-section">
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="12" :lg="6">
          <a-card class="kpi-card kpi-card-participants" :hoverable="true">
            <!-- 메인 KPI: 총 참여자 -->
            <div class="kpi-main-value">
              <div class="kpi-title-with-button">
                <div class="kpi-title">
                  <UserOutlined class="kpi-icon" />
                  <span>총 참여자</span>
                </div>
                <a-button
                  type="primary"
                  size="small"
                  class="kpi-calculate-btn"
                  @click="handleParticipantsCalculate"
                >
                  <template #icon><SyncOutlined /></template>
                  지표 계산
                </a-button>
              </div>
              <div class="editable-value-container">
                <template v-if="editingField === 'totalParticipants'">
                  <div class="adjustment-input-wrapper adjustment-input-active">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'totalParticipants')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('totalParticipants')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span class="main-value" :class="{ 'has-adjustment': hasAdjustment('totalParticipants') }">
                    {{ formatNumber(getAdjustedValue(kpiData.totalParticipants, 'totalParticipants')) }}명
                  </span>
                  <span v-if="hasAdjustment('totalParticipants')" class="adjustment-badge">
                    ({{ kpiData.totalParticipants }}{{ adjustments.totalParticipants >= 0 ? '+' : '' }}{{ adjustments.totalParticipants }})
                  </span>
                  <EditOutlined class="edit-icon" @click="startEditing('totalParticipants', adjustments.totalParticipants)" />
                </template>
              </div>
            </div>
            <div class="kpi-subtitle">이벤트 전체 참여 고객</div>

            <!-- 세그먼트별 분석 -->
            <div v-if="summary && summary.participants && summary.participants.bySegment" class="participants-breakdown">
              <div class="breakdown-section">
                <div class="breakdown-title">👥 인증서 보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 보유자 합계 -->
                  <template v-if="editingField === 'participants_certificateHolder_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'participants_certificateHolder_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_certificateHolder_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('participants_certificateHolder_total') }">
                      {{ formatNumber(getAdjustedValue(summary.participants.bySegment.certificateHolder.total.value, 'participants_certificateHolder_total')) }}명
                    </span>
                    <span v-if="hasAdjustment('participants_certificateHolder_total')" class="adjustment-badge-small">
                      ({{ summary.participants.bySegment.certificateHolder.total.value }}{{ adjustments.participants_certificateHolder_total >= 0 ? '+' : '' }}{{ adjustments.participants_certificateHolder_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('participants_certificateHolder_total', adjustments.participants_certificateHolder_total)" />
                  </template>
                  <span class="breakdown-inline-desc" v-if="summary.participants.bySegment.certificateHolder.total.description && summary.participants.bySegment.certificateHolder.total.description.trim()">
                    ({{ summary.participants.bySegment.certificateHolder.total.description }})
                  </span>
                </div>
                <div class="breakdown-items">
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 신규:</strong></span>
                    <!-- 신규 인증서 보유자 -->
                    <template v-if="editingField === 'participants_certificateHolder_new'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'participants_certificateHolder_new')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_certificateHolder_new')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('participants_certificateHolder_new') }">
                        {{ formatNumber(getAdjustedValue(summary.participants.bySegment.certificateHolder.newCertificate.value, 'participants_certificateHolder_new')) }}명
                      </span>
                      <span v-if="hasAdjustment('participants_certificateHolder_new')" class="adjustment-badge-tiny">
                        ({{ adjustments.participants_certificateHolder_new >= 0 ? '+' : '' }}{{ adjustments.participants_certificateHolder_new }})
                      </span>
                      <EditOutlined class="edit-icon-tiny" @click="startEditing('participants_certificateHolder_new', adjustments.participants_certificateHolder_new)" />
                    </template>
                    <span class="item-inline-desc" v-if="summary.participants.bySegment.certificateHolder.newCertificate.description && summary.participants.bySegment.certificateHolder.newCertificate.description.trim()">
                      ({{ summary.participants.bySegment.certificateHolder.newCertificate.description }})
                    </span>
                  </div>
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 기존:</strong></span>
                    <!-- 기존 인증서 보유자 -->
                    <template v-if="editingField === 'participants_certificateHolder_existing'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'participants_certificateHolder_existing')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_certificateHolder_existing')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('participants_certificateHolder_existing') }">
                        {{ formatNumber(getAdjustedValue(summary.participants.bySegment.certificateHolder.existingCertificate.value, 'participants_certificateHolder_existing')) }}명
                      </span>
                      <span v-if="hasAdjustment('participants_certificateHolder_existing')" class="adjustment-badge-tiny">
                        ({{ adjustments.participants_certificateHolder_existing >= 0 ? '+' : '' }}{{ adjustments.participants_certificateHolder_existing }})
                      </span>
                      <EditOutlined class="edit-icon-tiny" @click="startEditing('participants_certificateHolder_existing', adjustments.participants_certificateHolder_existing)" />
                    </template>
                    <span class="item-inline-desc" v-if="summary.participants.bySegment.certificateHolder.existingCertificate.description && summary.participants.bySegment.certificateHolder.existingCertificate.description.trim()">
                      ({{ summary.participants.bySegment.certificateHolder.existingCertificate.description }})
                    </span>
                  </div>
                </div>
              </div>

              <div class="breakdown-section">
                <div class="breakdown-title">🎯 인증서 미보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 미보유자 -->
                  <template v-if="editingField === 'participants_nonCertificate'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'participants_nonCertificate')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_nonCertificate')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('participants_nonCertificate') }">
                      {{ formatNumber(getAdjustedValue(summary.participants.bySegment.nonCertificate.value, 'participants_nonCertificate')) }}명
                    </span>
                    <span v-if="hasAdjustment('participants_nonCertificate')" class="adjustment-badge-small">
                      ({{ summary.participants.bySegment.nonCertificate.value }}{{ adjustments.participants_nonCertificate >= 0 ? '+' : '' }}{{ adjustments.participants_nonCertificate }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('participants_nonCertificate', adjustments.participants_nonCertificate)" />
                  </template>
                  <span class="breakdown-inline-desc" v-if="summary.participants.bySegment.nonCertificate.description && summary.participants.bySegment.nonCertificate.description.trim()">
                    ({{ summary.participants.bySegment.nonCertificate.description }})
                  </span>
                </div>
                <div class="breakdown-items">
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 써마지:</strong></span>
                    <template v-if="editingField === 'participants_nonCertificate_thermage'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'participants_nonCertificate_thermage')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_nonCertificate_thermage')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('participants_nonCertificate_thermage') }">
                        {{ formatNumber(getAdjustedValue(4733, 'participants_nonCertificate_thermage')) }}명
                      </span>
                      <span v-if="hasAdjustment('participants_nonCertificate_thermage')" class="adjustment-badge-small">
                        (4733{{ adjustments.participants_nonCertificate_thermage >= 0 ? '+' : '' }}{{ adjustments.participants_nonCertificate_thermage }})
                      </span>
                      <EditOutlined class="edit-icon-small" @click="startEditing('participants_nonCertificate_thermage', adjustments.participants_nonCertificate_thermage)" />
                    </template>
                  </div>
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 공유참여:</strong></span>
                    <template v-if="editingField === 'participants_nonCertificate_shareParticipation'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'participants_nonCertificate_shareParticipation')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('participants_nonCertificate_shareParticipation')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('participants_nonCertificate_shareParticipation') }">
                        {{ formatNumber(getAdjustedValue(summary.participants.bySegment.nonCertificate.value - 4733, 'participants_nonCertificate_shareParticipation')) }}명
                      </span>
                      <span v-if="hasAdjustment('participants_nonCertificate_shareParticipation')" class="adjustment-badge-small">
                        ({{ summary.participants.bySegment.nonCertificate.value - 4733 }}{{ adjustments.participants_nonCertificate_shareParticipation >= 0 ? '+' : '' }}{{ adjustments.participants_nonCertificate_shareParticipation }})
                      </span>
                      <EditOutlined class="edit-icon-small" @click="startEditing('participants_nonCertificate_shareParticipation', adjustments.participants_nonCertificate_shareParticipation)" />
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="12" :lg="6">
          <a-card class="kpi-card kpi-card-reviews" :hoverable="true">
            <!-- 메인 KPI: 후기 작성 -->
            <div class="kpi-main-value">
              <div class="kpi-title-with-button">
                <div class="kpi-title kpi-title-reviews">
                  <EditOutlined class="kpi-icon" />
                  <span>후기 작성</span>
                </div>
                <a-button
                  type="primary"
                  size="small"
                  class="kpi-calculate-btn"
                  @click="handleReviewsCalculate"
                >
                  <template #icon><SyncOutlined /></template>
                  지표 계산
                </a-button>
              </div>
              <div class="editable-value-container">
                <template v-if="editingField === 'totalReviews'">
                  <div class="adjustment-input-wrapper adjustment-input-active">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'totalReviews')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('totalReviews')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span class="main-value main-value-reviews" :class="{ 'has-adjustment': hasAdjustment('totalReviews') }">
                    {{ formatNumber(getAdjustedValue(kpiData.totalReviews, 'totalReviews')) }}건
                  </span>
                  <span v-if="hasAdjustment('totalReviews')" class="adjustment-badge">
                    ({{ kpiData.totalReviews }}{{ adjustments.totalReviews >= 0 ? '+' : '' }}{{ adjustments.totalReviews }})
                  </span>
                  <EditOutlined class="edit-icon" @click="startEditing('totalReviews', adjustments.totalReviews)" />
                </template>
              </div>
            </div>
            <div class="kpi-subtitle">전체 후기 작성수</div>

            <!-- 세그먼트별 분석 -->
            <div v-if="summary && summary.reviews && summary.reviews.bySegment" class="reviews-breakdown">
              <div class="breakdown-section">
                <div class="breakdown-title">👥 인증서 보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 보유자 후기 합계 -->
                  <template v-if="editingField === 'reviews_certificateHolder_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'reviews_certificateHolder_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reviews_certificateHolder_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('reviews_certificateHolder_total') }">
                      {{ formatNumber(getAdjustedValue(summary.reviews.bySegment.certificateHolder.total.value, 'reviews_certificateHolder_total')) }}건
                    </span>
                    <span v-if="hasAdjustment('reviews_certificateHolder_total')" class="adjustment-badge-small">
                      ({{ summary.reviews.bySegment.certificateHolder.total.value }}{{ adjustments.reviews_certificateHolder_total >= 0 ? '+' : '' }}{{ adjustments.reviews_certificateHolder_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('reviews_certificateHolder_total', adjustments.reviews_certificateHolder_total)" />
                  </template>
                  <span class="breakdown-inline-desc" v-if="summary.reviews.bySegment.certificateHolder.total.description && summary.reviews.bySegment.certificateHolder.total.description.trim()">
                    ({{ summary.reviews.bySegment.certificateHolder.total.description }})
                  </span>
                </div>
                <div class="breakdown-items">
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 신규:</strong></span>
                    <!-- 신규 인증서 보유자 후기 -->
                    <template v-if="editingField === 'reviews_certificateHolder_new'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'reviews_certificateHolder_new')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reviews_certificateHolder_new')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('reviews_certificateHolder_new') }">
                        {{ formatNumber(getAdjustedValue(summary.reviews.bySegment.certificateHolder.newCertificate.value, 'reviews_certificateHolder_new')) }}건
                      </span>
                      <span v-if="hasAdjustment('reviews_certificateHolder_new')" class="adjustment-badge-tiny">
                        ({{ adjustments.reviews_certificateHolder_new >= 0 ? '+' : '' }}{{ adjustments.reviews_certificateHolder_new }})
                      </span>
                      <EditOutlined class="edit-icon-tiny" @click="startEditing('reviews_certificateHolder_new', adjustments.reviews_certificateHolder_new)" />
                    </template>
                    <span class="item-inline-desc" v-if="summary.reviews.bySegment.certificateHolder.newCertificate.description && summary.reviews.bySegment.certificateHolder.newCertificate.description.trim()">
                      ({{ summary.reviews.bySegment.certificateHolder.newCertificate.description }})
                    </span>
                  </div>
                  <div class="breakdown-item">
                    <span class="item-label"><strong>• 기존:</strong></span>
                    <!-- 기존 인증서 보유자 후기 -->
                    <template v-if="editingField === 'reviews_certificateHolder_existing'">
                      <div class="adjustment-input-wrapper adjustment-input-active inline">
                        <a-input-number
                          v-model:value="tempInputValue"
                          size="small"
                          :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                          @keydown="handleKeydown($event, 'reviews_certificateHolder_existing')"
                        />
                        <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reviews_certificateHolder_existing')" />
                        <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                      </div>
                    </template>
                    <template v-else>
                      <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('reviews_certificateHolder_existing') }">
                        {{ formatNumber(getAdjustedValue(summary.reviews.bySegment.certificateHolder.existingCertificate.value, 'reviews_certificateHolder_existing')) }}건
                      </span>
                      <span v-if="hasAdjustment('reviews_certificateHolder_existing')" class="adjustment-badge-tiny">
                        ({{ adjustments.reviews_certificateHolder_existing >= 0 ? '+' : '' }}{{ adjustments.reviews_certificateHolder_existing }})
                      </span>
                      <EditOutlined class="edit-icon-tiny" @click="startEditing('reviews_certificateHolder_existing', adjustments.reviews_certificateHolder_existing)" />
                    </template>
                    <span class="item-inline-desc" v-if="summary.reviews.bySegment.certificateHolder.existingCertificate.description && summary.reviews.bySegment.certificateHolder.existingCertificate.description.trim()">
                      ({{ summary.reviews.bySegment.certificateHolder.existingCertificate.description }})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="12" :lg="6">
          <a-card class="kpi-card kpi-card-shares" :hoverable="true">
            <!-- 메인 KPI: 공유 -->
            <div class="kpi-main-value">
              <div class="kpi-title-with-button">
                <div class="kpi-title kpi-title-shares">
                  <ShareAltOutlined class="kpi-icon" />
                  <span>공유</span>
                </div>
                <a-button
                  type="primary"
                  size="small"
                  class="kpi-calculate-btn"
                  @click="handleSharesCalculate"
                >
                  <template #icon><SyncOutlined /></template>
                  지표 계산
                </a-button>
              </div>
              <div class="editable-value-container">
                <template v-if="editingField === 'totalShares'">
                  <div class="adjustment-input-wrapper adjustment-input-active">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'totalShares')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('totalShares')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span class="main-value main-value-shares" :class="{ 'has-adjustment': hasAdjustment('totalShares') }">
                    {{ formatNumber(getAdjustedValue(shareAnalysisData.totalShareCount, 'totalShares')) }}회
                  </span>
                  <span v-if="hasAdjustment('totalShares')" class="adjustment-badge">
                    ({{ shareAnalysisData.totalShareCount }}{{ adjustments.totalShares >= 0 ? '+' : '' }}{{ adjustments.totalShares }})
                  </span>
                  <EditOutlined class="edit-icon" @click="startEditing('totalShares', adjustments.totalShares)" />
                </template>
              </div>
              <div class="sub-stat-row" style="font-size: 14px; color: #8c8c8c; font-weight: 500; margin-top: 4px; display: flex; align-items: center; gap: 4px;">
                (<template v-if="editingField === 'shares_totalCustomers'">
                  <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'shares_totalCustomers')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_totalCustomers')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span :class="{ 'has-adjustment': hasAdjustment('shares_totalCustomers') }">
                    {{ formatNumber(getAdjustedValue(shareAnalysisData.totalShareCustomers, 'shares_totalCustomers')) }}명
                  </span>
                  <span v-if="hasAdjustment('shares_totalCustomers')" class="adjustment-badge-small">
                    ({{ shareAnalysisData.totalShareCustomers }}{{ adjustments.shares_totalCustomers >= 0 ? '+' : '' }}{{ adjustments.shares_totalCustomers }})
                  </span>
                  <EditOutlined class="edit-icon-small" @click="startEditing('shares_totalCustomers', adjustments.shares_totalCustomers)" />
                </template>, {{ getAdjustedValue(shareAnalysisData.totalShareCustomers, 'shares_totalCustomers') > 0 ? (getAdjustedValue(shareAnalysisData.totalShareCount, 'totalShares') / getAdjustedValue(shareAnalysisData.totalShareCustomers, 'shares_totalCustomers')).toFixed(2) : '0.00' }}회/명)
              </div>
            </div>
            <div class="kpi-subtitle">전체 공유수</div>

            <!-- 세그먼트별 분석 -->
            <div v-if="shareAnalysisData.hasCertificateHolder || shareAnalysisData.hasNonCertificate" class="shares-breakdown">
              <div v-if="shareAnalysisData.hasCertificateHolder" class="breakdown-section">
                <div class="breakdown-title">👥 인증서 보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 보유자 공유 -->
                  <template v-if="editingField === 'shares_certificateHolder_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'shares_certificateHolder_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_certificateHolder_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('shares_certificateHolder_total') }">
                      {{ formatNumber(getAdjustedValue(shareAnalysisData.certificateHolder.shareCount, 'shares_certificateHolder_total')) }}회
                    </span>
                    <span v-if="hasAdjustment('shares_certificateHolder_total')" class="adjustment-badge-small">
                      ({{ shareAnalysisData.certificateHolder.shareCount }}{{ adjustments.shares_certificateHolder_total >= 0 ? '+' : '' }}{{ adjustments.shares_certificateHolder_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('shares_certificateHolder_total', adjustments.shares_certificateHolder_total)" />
                  </template>
                  <span class="breakdown-inline-desc">
                    {{ formatNumber(shareAnalysisData.certificateHolder.shareCustomers) }}명, {{ shareAnalysisData.certificateHolder.shareCustomers > 0 ? (getAdjustedValue(shareAnalysisData.certificateHolder.shareCount, 'shares_certificateHolder_total') / shareAnalysisData.certificateHolder.shareCustomers).toFixed(2) : '0.00' }}회/명
                  </span>
                </div>
                <!-- 세컨더리 세그먼트 -->
                <div v-if="shareAnalysisData.certificateHolder.secondary.length > 0" class="breakdown-items">
                  <div v-for="(seg, idx) in shareAnalysisData.certificateHolder.secondary" :key="idx" class="breakdown-item">
                    <span class="item-label"><strong>• {{ seg.label }}:</strong></span>
                    <!-- 기존 인증서 -->
                    <template v-if="seg.label === '기존 인증서'">
                      <template v-if="editingField === 'shares_certificateHolder_existing'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'shares_certificateHolder_existing')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_certificateHolder_existing')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('shares_certificateHolder_existing') }">
                          {{ formatNumber(getAdjustedValue(seg.shareCount, 'shares_certificateHolder_existing')) }}회
                        </span>
                        <span v-if="hasAdjustment('shares_certificateHolder_existing')" class="adjustment-badge-small">
                          ({{ seg.shareCount }}{{ adjustments.shares_certificateHolder_existing >= 0 ? '+' : '' }}{{ adjustments.shares_certificateHolder_existing }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('shares_certificateHolder_existing', adjustments.shares_certificateHolder_existing)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'shares_certificateHolder_existing_customers'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'shares_certificateHolder_existing_customers')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_certificateHolder_existing_customers')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('shares_certificateHolder_existing_customers') }">
                            {{ formatNumber(getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_existing_customers')) }}명
                          </span>
                          <span v-if="hasAdjustment('shares_certificateHolder_existing_customers')" class="adjustment-badge-small">
                            ({{ seg.shareCustomers }}{{ adjustments.shares_certificateHolder_existing_customers >= 0 ? '+' : '' }}{{ adjustments.shares_certificateHolder_existing_customers }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('shares_certificateHolder_existing_customers', adjustments.shares_certificateHolder_existing_customers)" />
                        </template>, {{ getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_existing_customers') > 0 ? (getAdjustedValue(seg.shareCount, 'shares_certificateHolder_existing') / getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_existing_customers')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 신규 인증서 -->
                    <template v-else-if="seg.label === '신규 인증서'">
                      <template v-if="editingField === 'shares_certificateHolder_new'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'shares_certificateHolder_new')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_certificateHolder_new')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('shares_certificateHolder_new') }">
                          {{ formatNumber(getAdjustedValue(seg.shareCount, 'shares_certificateHolder_new')) }}회
                        </span>
                        <span v-if="hasAdjustment('shares_certificateHolder_new')" class="adjustment-badge-small">
                          ({{ seg.shareCount }}{{ adjustments.shares_certificateHolder_new >= 0 ? '+' : '' }}{{ adjustments.shares_certificateHolder_new }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('shares_certificateHolder_new', adjustments.shares_certificateHolder_new)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'shares_certificateHolder_new_customers'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'shares_certificateHolder_new_customers')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_certificateHolder_new_customers')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('shares_certificateHolder_new_customers') }">
                            {{ formatNumber(getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_new_customers')) }}명
                          </span>
                          <span v-if="hasAdjustment('shares_certificateHolder_new_customers')" class="adjustment-badge-small">
                            ({{ seg.shareCustomers }}{{ adjustments.shares_certificateHolder_new_customers >= 0 ? '+' : '' }}{{ adjustments.shares_certificateHolder_new_customers }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('shares_certificateHolder_new_customers', adjustments.shares_certificateHolder_new_customers)" />
                        </template>, {{ getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_new_customers') > 0 ? (getAdjustedValue(seg.shareCount, 'shares_certificateHolder_new') / getAdjustedValue(seg.shareCustomers, 'shares_certificateHolder_new_customers')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 기타 (기본 표시) -->
                    <template v-else>
                      <span class="item-value">{{ formatNumber(seg.shareCount) }}회</span>
                      <span class="item-inline-desc">
                        ({{ formatNumber(seg.shareCustomers) }}명, {{ seg.shareCustomers > 0 ? (seg.shareCount / seg.shareCustomers).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                  </div>
                </div>
              </div>

              <div v-if="shareAnalysisData.hasNonCertificate" class="breakdown-section">
                <div class="breakdown-title">🎯 인증서 미보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 미보유자 공유 -->
                  <template v-if="editingField === 'shares_nonCertificate_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'shares_nonCertificate_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_nonCertificate_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('shares_nonCertificate_total') }">
                      {{ formatNumber(getAdjustedValue(shareAnalysisData.nonCertificate.shareCount, 'shares_nonCertificate_total')) }}회
                    </span>
                    <span v-if="hasAdjustment('shares_nonCertificate_total')" class="adjustment-badge-small">
                      ({{ shareAnalysisData.nonCertificate.shareCount }}{{ adjustments.shares_nonCertificate_total >= 0 ? '+' : '' }}{{ adjustments.shares_nonCertificate_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('shares_nonCertificate_total', adjustments.shares_nonCertificate_total)" />
                  </template>
                  <span class="breakdown-inline-desc">
                    {{ formatNumber(shareAnalysisData.nonCertificate.shareCustomers) }}명, {{ shareAnalysisData.nonCertificate.shareCustomers > 0 ? (getAdjustedValue(shareAnalysisData.nonCertificate.shareCount, 'shares_nonCertificate_total') / shareAnalysisData.nonCertificate.shareCustomers).toFixed(2) : '0.00' }}회/명
                  </span>
                </div>
                <!-- 세컨더리 세그먼트 -->
                <div v-if="shareAnalysisData.nonCertificate.secondary.length > 0" class="breakdown-items">
                  <div v-for="(seg, idx) in shareAnalysisData.nonCertificate.secondary" :key="idx" class="breakdown-item">
                    <span class="item-label"><strong>• {{ seg.label }}:</strong></span>
                    <!-- 이벤트 참여자 -->
                    <template v-if="seg.label === '이벤트 참여자'">
                      <template v-if="editingField === 'shares_nonCertificate_eventParticipant'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'shares_nonCertificate_eventParticipant')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_nonCertificate_eventParticipant')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('shares_nonCertificate_eventParticipant') }">
                          {{ formatNumber(getAdjustedValue(seg.shareCount, 'shares_nonCertificate_eventParticipant')) }}회
                        </span>
                        <span v-if="hasAdjustment('shares_nonCertificate_eventParticipant')" class="adjustment-badge-small">
                          ({{ seg.shareCount }}{{ adjustments.shares_nonCertificate_eventParticipant >= 0 ? '+' : '' }}{{ adjustments.shares_nonCertificate_eventParticipant }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('shares_nonCertificate_eventParticipant', adjustments.shares_nonCertificate_eventParticipant)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'shares_nonCertificate_eventParticipant_customers'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'shares_nonCertificate_eventParticipant_customers')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_nonCertificate_eventParticipant_customers')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('shares_nonCertificate_eventParticipant_customers') }">
                            {{ formatNumber(getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_eventParticipant_customers')) }}명
                          </span>
                          <span v-if="hasAdjustment('shares_nonCertificate_eventParticipant_customers')" class="adjustment-badge-small">
                            ({{ seg.shareCustomers }}{{ adjustments.shares_nonCertificate_eventParticipant_customers >= 0 ? '+' : '' }}{{ adjustments.shares_nonCertificate_eventParticipant_customers }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('shares_nonCertificate_eventParticipant_customers', adjustments.shares_nonCertificate_eventParticipant_customers)" />
                        </template>, {{ getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_eventParticipant_customers') > 0 ? (getAdjustedValue(seg.shareCount, 'shares_nonCertificate_eventParticipant') / getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_eventParticipant_customers')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 써마지 -->
                    <template v-else-if="seg.label === '써마지'">
                      <template v-if="editingField === 'shares_nonCertificate_thermage'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'shares_nonCertificate_thermage')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_nonCertificate_thermage')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('shares_nonCertificate_thermage') }">
                          {{ formatNumber(getAdjustedValue(seg.shareCount, 'shares_nonCertificate_thermage')) }}회
                        </span>
                        <span v-if="hasAdjustment('shares_nonCertificate_thermage')" class="adjustment-badge-small">
                          ({{ seg.shareCount }}{{ adjustments.shares_nonCertificate_thermage >= 0 ? '+' : '' }}{{ adjustments.shares_nonCertificate_thermage }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('shares_nonCertificate_thermage', adjustments.shares_nonCertificate_thermage)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'shares_nonCertificate_thermage_customers'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'shares_nonCertificate_thermage_customers')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('shares_nonCertificate_thermage_customers')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('shares_nonCertificate_thermage_customers') }">
                            {{ formatNumber(getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_thermage_customers')) }}명
                          </span>
                          <span v-if="hasAdjustment('shares_nonCertificate_thermage_customers')" class="adjustment-badge-small">
                            ({{ seg.shareCustomers }}{{ adjustments.shares_nonCertificate_thermage_customers >= 0 ? '+' : '' }}{{ adjustments.shares_nonCertificate_thermage_customers }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('shares_nonCertificate_thermage_customers', adjustments.shares_nonCertificate_thermage_customers)" />
                        </template>, {{ getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_thermage_customers') > 0 ? (getAdjustedValue(seg.shareCount, 'shares_nonCertificate_thermage') / getAdjustedValue(seg.shareCustomers, 'shares_nonCertificate_thermage_customers')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 기타 (기본 표시) -->
                    <template v-else>
                      <span class="item-value">{{ formatNumber(seg.shareCount) }}회</span>
                      <span class="item-inline-desc">
                        ({{ formatNumber(seg.shareCustomers) }}명, {{ seg.shareCustomers > 0 ? (seg.shareCount / seg.shareCustomers).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="12" :lg="6">
          <a-card class="kpi-card kpi-card-coupons" :hoverable="true">
            <!-- 메인 KPI: 쿠폰 발급 -->
            <div class="kpi-main-value">
              <div class="kpi-title-with-button">
                <div class="kpi-title kpi-title-coupons">
                  <GiftOutlined class="kpi-icon" />
                  <span>쿠폰 발급</span>
                </div>
                <a-button
                  type="primary"
                  size="small"
                  class="kpi-calculate-btn"
                  :loading="couponApiLoading"
                  @click="handleCouponsCalculate"
                >
                  <template #icon><SyncOutlined /></template>
                  지표 계산
                </a-button>
              </div>
              <div class="editable-value-container">
                <template v-if="editingField === 'totalCoupons'">
                  <div class="adjustment-input-wrapper adjustment-input-active">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'totalCoupons')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('totalCoupons')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span class="main-value main-value-coupons" :class="{ 'has-adjustment': hasAdjustment('totalCoupons') }">
                    {{ formatNumber(getAdjustedValue(couponAnalysisData.totalCouponIssuances, 'totalCoupons')) }}회
                  </span>
                  <span v-if="hasAdjustment('totalCoupons')" class="adjustment-badge">
                    ({{ couponAnalysisData.totalCouponIssuances }}{{ adjustments.totalCoupons >= 0 ? '+' : '' }}{{ adjustments.totalCoupons }})
                  </span>
                  <EditOutlined class="edit-icon" @click="startEditing('totalCoupons', adjustments.totalCoupons)" />
                </template>
              </div>
              <div class="sub-stat-row" style="font-size: 14px; color: #8c8c8c; font-weight: 500; margin-top: 4px; display: flex; align-items: center; gap: 4px;">
                (<template v-if="editingField === 'coupons_totalRecipients'">
                  <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                    <a-input-number
                      v-model:value="tempInputValue"
                      size="small"
                      :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                      @keydown="handleKeydown($event, 'coupons_totalRecipients')"
                    />
                    <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_totalRecipients')" />
                    <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                  </div>
                </template>
                <template v-else>
                  <span :class="{ 'has-adjustment': hasAdjustment('coupons_totalRecipients') }">
                    {{ formatNumber(getAdjustedValue(couponAnalysisData.totalCouponRecipients, 'coupons_totalRecipients')) }}명
                  </span>
                  <span v-if="hasAdjustment('coupons_totalRecipients')" class="adjustment-badge-small">
                    ({{ couponAnalysisData.totalCouponRecipients }}{{ adjustments.coupons_totalRecipients >= 0 ? '+' : '' }}{{ adjustments.coupons_totalRecipients }})
                  </span>
                  <EditOutlined class="edit-icon-small" @click="startEditing('coupons_totalRecipients', adjustments.coupons_totalRecipients)" />
                </template>, {{ getAdjustedValue(couponAnalysisData.totalCouponRecipients, 'coupons_totalRecipients') > 0 ? (getAdjustedValue(couponAnalysisData.totalCouponIssuances, 'totalCoupons') / getAdjustedValue(couponAnalysisData.totalCouponRecipients, 'coupons_totalRecipients')).toFixed(2) : '0.00' }}회/명)
              </div>
            </div>
            <div class="kpi-subtitle">전체 쿠폰 발급량</div>

            <!-- 세그먼트별 분석 -->
            <div v-if="couponAnalysisData.hasCertificateHolder || couponAnalysisData.hasNonCertificate" class="coupons-breakdown">
              <div v-if="couponAnalysisData.hasCertificateHolder" class="breakdown-section">
                <div class="breakdown-title">👥 인증서 보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 보유자 쿠폰 -->
                  <template v-if="editingField === 'coupons_certificateHolder_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'coupons_certificateHolder_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_certificateHolder_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('coupons_certificateHolder_total') }">
                      {{ formatNumber(getAdjustedValue(couponAnalysisData.certificateHolder.couponIssuances, 'coupons_certificateHolder_total')) }}회
                    </span>
                    <span v-if="hasAdjustment('coupons_certificateHolder_total')" class="adjustment-badge-small">
                      ({{ couponAnalysisData.certificateHolder.couponIssuances }}{{ adjustments.coupons_certificateHolder_total >= 0 ? '+' : '' }}{{ adjustments.coupons_certificateHolder_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('coupons_certificateHolder_total', adjustments.coupons_certificateHolder_total)" />
                  </template>
                  <span class="breakdown-inline-desc">
                    {{ formatNumber(couponAnalysisData.certificateHolder.couponRecipients) }}명, {{ couponAnalysisData.certificateHolder.couponRecipients > 0 ? (getAdjustedValue(couponAnalysisData.certificateHolder.couponIssuances, 'coupons_certificateHolder_total') / couponAnalysisData.certificateHolder.couponRecipients).toFixed(2) : '0.00' }}회/명
                  </span>
                </div>
                <div v-if="couponAnalysisData.certificateHolder.secondary.length > 0" class="breakdown-items">
                  <div v-for="(seg, idx) in couponAnalysisData.certificateHolder.secondary" :key="idx" class="breakdown-item">
                    <span class="item-label"><strong>• {{ seg.label }}:</strong></span>
                    <!-- 기존 인증서 -->
                    <template v-if="seg.label === '기존 인증서'">
                      <template v-if="editingField === 'coupons_certificateHolder_existing'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'coupons_certificateHolder_existing')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_certificateHolder_existing')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('coupons_certificateHolder_existing') }">
                          {{ formatNumber(getAdjustedValue(seg.couponIssuances, 'coupons_certificateHolder_existing')) }}회
                        </span>
                        <span v-if="hasAdjustment('coupons_certificateHolder_existing')" class="adjustment-badge-small">
                          ({{ seg.couponIssuances }}{{ adjustments.coupons_certificateHolder_existing >= 0 ? '+' : '' }}{{ adjustments.coupons_certificateHolder_existing }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('coupons_certificateHolder_existing', adjustments.coupons_certificateHolder_existing)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'coupons_certificateHolder_existing_recipients'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'coupons_certificateHolder_existing_recipients')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_certificateHolder_existing_recipients')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('coupons_certificateHolder_existing_recipients') }">
                            {{ formatNumber(getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_existing_recipients')) }}명
                          </span>
                          <span v-if="hasAdjustment('coupons_certificateHolder_existing_recipients')" class="adjustment-badge-small">
                            ({{ seg.couponRecipients }}{{ adjustments.coupons_certificateHolder_existing_recipients >= 0 ? '+' : '' }}{{ adjustments.coupons_certificateHolder_existing_recipients }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('coupons_certificateHolder_existing_recipients', adjustments.coupons_certificateHolder_existing_recipients)" />
                        </template>, {{ getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_existing_recipients') > 0 ? (getAdjustedValue(seg.couponIssuances, 'coupons_certificateHolder_existing') / getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_existing_recipients')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 신규 인증서 -->
                    <template v-else-if="seg.label === '신규 인증서'">
                      <template v-if="editingField === 'coupons_certificateHolder_new'">
                        <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                          <a-input-number
                            v-model:value="tempInputValue"
                            size="small"
                            :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                            @keydown="handleKeydown($event, 'coupons_certificateHolder_new')"
                          />
                          <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_certificateHolder_new')" />
                          <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                        </div>
                      </template>
                      <template v-else>
                        <span class="item-value" :class="{ 'has-adjustment': hasAdjustment('coupons_certificateHolder_new') }">
                          {{ formatNumber(getAdjustedValue(seg.couponIssuances, 'coupons_certificateHolder_new')) }}회
                        </span>
                        <span v-if="hasAdjustment('coupons_certificateHolder_new')" class="adjustment-badge-small">
                          ({{ seg.couponIssuances }}{{ adjustments.coupons_certificateHolder_new >= 0 ? '+' : '' }}{{ adjustments.coupons_certificateHolder_new }})
                        </span>
                        <EditOutlined class="edit-icon-small" @click="startEditing('coupons_certificateHolder_new', adjustments.coupons_certificateHolder_new)" />
                      </template>
                      <span class="item-inline-desc">
                        (<template v-if="editingField === 'coupons_certificateHolder_new_recipients'">
                          <div class="adjustment-input-wrapper adjustment-input-active inline-input">
                            <a-input-number
                              v-model:value="tempInputValue"
                              size="small"
                              :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                              @keydown="handleKeydown($event, 'coupons_certificateHolder_new_recipients')"
                            />
                            <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_certificateHolder_new_recipients')" />
                            <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                          </div>
                        </template>
                        <template v-else>
                          <span :class="{ 'has-adjustment': hasAdjustment('coupons_certificateHolder_new_recipients') }">
                            {{ formatNumber(getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_new_recipients')) }}명
                          </span>
                          <span v-if="hasAdjustment('coupons_certificateHolder_new_recipients')" class="adjustment-badge-small">
                            ({{ seg.couponRecipients }}{{ adjustments.coupons_certificateHolder_new_recipients >= 0 ? '+' : '' }}{{ adjustments.coupons_certificateHolder_new_recipients }})
                          </span>
                          <EditOutlined class="edit-icon-small" @click="startEditing('coupons_certificateHolder_new_recipients', adjustments.coupons_certificateHolder_new_recipients)" />
                        </template>, {{ getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_new_recipients') > 0 ? (getAdjustedValue(seg.couponIssuances, 'coupons_certificateHolder_new') / getAdjustedValue(seg.couponRecipients, 'coupons_certificateHolder_new_recipients')).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                    <!-- 기타 (기본 표시) -->
                    <template v-else>
                      <span class="item-value">{{ formatNumber(seg.couponIssuances) }}회</span>
                      <span class="item-inline-desc">
                        ({{ formatNumber(seg.couponRecipients) }}명, {{ seg.couponRecipients > 0 ? (seg.couponIssuances / seg.couponRecipients).toFixed(2) : '0.00' }}회/명)
                      </span>
                    </template>
                  </div>
                </div>
              </div>

              <div v-if="couponAnalysisData.hasNonCertificate" class="breakdown-section">
                <div class="breakdown-title">🎯 인증서 미보유자</div>
                <div class="breakdown-value-row">
                  <!-- 인증서 미보유자 쿠폰 -->
                  <template v-if="editingField === 'coupons_nonCertificate_total'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'coupons_nonCertificate_total')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('coupons_nonCertificate_total')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <span class="breakdown-value" :class="{ 'has-adjustment': hasAdjustment('coupons_nonCertificate_total') }">
                      {{ formatNumber(getAdjustedValue(couponAnalysisData.nonCertificate.couponIssuances, 'coupons_nonCertificate_total')) }}회
                    </span>
                    <span v-if="hasAdjustment('coupons_nonCertificate_total')" class="adjustment-badge-small">
                      ({{ couponAnalysisData.nonCertificate.couponIssuances }}{{ adjustments.coupons_nonCertificate_total >= 0 ? '+' : '' }}{{ adjustments.coupons_nonCertificate_total }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('coupons_nonCertificate_total', adjustments.coupons_nonCertificate_total)" />
                  </template>
                  <span class="breakdown-inline-desc">
                    {{ formatNumber(couponAnalysisData.nonCertificate.couponRecipients) }}명, {{ couponAnalysisData.nonCertificate.couponRecipients > 0 ? (getAdjustedValue(couponAnalysisData.nonCertificate.couponIssuances, 'coupons_nonCertificate_total') / couponAnalysisData.nonCertificate.couponRecipients).toFixed(2) : '0.00' }}회/명
                  </span>
                </div>
                <!-- 세컨더리 세그먼트 -->
                <div v-if="couponAnalysisData.nonCertificate.secondary.length > 0" class="breakdown-items">
                  <div v-for="(seg, idx) in couponAnalysisData.nonCertificate.secondary" :key="idx" class="breakdown-item">
                    <span class="item-label"><strong>• {{ seg.label }}:</strong></span>
                    <span class="item-value">{{ formatNumber(seg.couponIssuances) }}회</span>
                    <span class="item-inline-desc">
                      ({{ formatNumber(seg.couponRecipients) }}명, {{ seg.couponRecipients > 0 ? (seg.couponIssuances / seg.couponRecipients).toFixed(2) : '0.00' }}회/명)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 병원 예약 섹션 (별도 행) -->
    <div class="reservation-section">
      <div class="section-header-row">
        <h3 class="section-header">📅 병원 예약 현황</h3>
        <a-button
          type="primary"
          size="small"
          :loading="reservationApiLoading"
          @click="fetchReservationStatistics"
        >
          <template #icon><SyncOutlined /></template>
          지표 계산
        </a-button>
      </div>
      <a-row :gutter="[16, 16]">
        <!-- 서머리 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="reservation-card summary-card" :hoverable="true">
            <div class="card-title">📊 예약 서머리</div>
            <div class="summary-stats">
              <div class="stat-item">
                <div class="stat-label">전체 예약 건수</div>
                <div class="editable-stat-value">
                  <template v-if="editingField === 'reservation_totalReservations'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'reservation_totalReservations')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reservation_totalReservations')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="stat-value total-count" :class="{ 'has-adjustment': hasAdjustment('reservation_totalReservations') }">
                      {{ formatNumber(getAdjustedValue(reservationSummary.totalReservations, 'reservation_totalReservations')) }}
                    </div>
                    <span v-if="hasAdjustment('reservation_totalReservations')" class="adjustment-badge-small">
                      ({{ reservationSummary.totalReservations }}{{ adjustments.reservation_totalReservations >= 0 ? '+' : '' }}{{ adjustments.reservation_totalReservations }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('reservation_totalReservations', adjustments.reservation_totalReservations)" />
                  </template>
                </div>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <div class="stat-label">유니크 사용자 수</div>
                <div class="editable-stat-value">
                  <template v-if="editingField === 'reservation_uniquePatients'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'reservation_uniquePatients')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reservation_uniquePatients')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="stat-value unique-count" :class="{ 'has-adjustment': hasAdjustment('reservation_uniquePatients') }">
                      {{ formatNumber(getAdjustedValue(reservationSummary.uniquePatients, 'reservation_uniquePatients')) }}
                    </div>
                    <span v-if="hasAdjustment('reservation_uniquePatients')" class="adjustment-badge-small">
                      ({{ reservationSummary.uniquePatients }}{{ adjustments.reservation_uniquePatients >= 0 ? '+' : '' }}{{ adjustments.reservation_uniquePatients }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('reservation_uniquePatients', adjustments.reservation_uniquePatients)" />
                  </template>
                </div>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <div class="stat-label">예약 병원 수</div>
                <div class="editable-stat-value">
                  <template v-if="editingField === 'reservation_totalHospitals'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'reservation_totalHospitals')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reservation_totalHospitals')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="stat-value hospital-count" :class="{ 'has-adjustment': hasAdjustment('reservation_totalHospitals') }">
                      {{ formatNumber(getAdjustedValue(reservationSummary.totalHospitals, 'reservation_totalHospitals')) }}
                    </div>
                    <span v-if="hasAdjustment('reservation_totalHospitals')" class="adjustment-badge-small">
                      ({{ reservationSummary.totalHospitals }}{{ adjustments.reservation_totalHospitals >= 0 ? '+' : '' }}{{ adjustments.reservation_totalHospitals }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('reservation_totalHospitals', adjustments.reservation_totalHospitals)" />
                  </template>
                </div>
              </div>
            </div>
          </a-card>
        </a-col>

        <!-- 일자별 예약 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="reservation-card daily-card" :hoverable="true">
            <div class="card-title">📆 일자별 예약</div>
            <div class="daily-list">
              <div v-for="(item, index) in dailyReservations" :key="index" class="daily-item">
                <div class="daily-date">{{ formatDate(item.date) }}</div>
                <div class="daily-stats">
                  <span class="daily-count">{{ item.count }}건</span>
                  <span class="daily-users">({{ item.uniqueUsers }}명)</span>
                </div>
              </div>
              <div v-if="dailyReservations.length === 0" class="empty-message">
                예약 데이터가 없습니다.
              </div>
            </div>
          </a-card>
        </a-col>

        <!-- 병원별 예약 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="reservation-card hospital-card" :hoverable="true">
            <div class="card-title">🏥 병원별 예약</div>
            <div class="hospital-list">
              <div v-for="(item, index) in hospitalReservations" :key="index" class="hospital-item">
                <div class="hospital-name">{{ item.hospitalName }}</div>
                <div class="hospital-count">{{ item.count }}건</div>
              </div>
              <div v-if="hospitalReservations.length === 0" class="empty-message">
                예약 데이터가 없습니다.
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 리뷰 현황 섹션 -->
    <div class="review-section">
      <div class="section-header-row">
        <h3 class="section-header">✍️ 리뷰 현황</h3>
        <a-button
          type="primary"
          size="small"
          :loading="reviewApiLoading"
          @click="fetchReviewStatistics"
        >
          <template #icon><SyncOutlined /></template>
          지표 계산
        </a-button>
      </div>
      <a-row :gutter="[16, 16]">
        <!-- 서머리 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="review-card summary-card" :hoverable="true">
            <div class="card-title">📊 리뷰 서머리</div>
            <div class="summary-stats">
              <div class="stat-item">
                <div class="stat-label">전체 리뷰 수</div>
                <div class="editable-stat-value">
                  <template v-if="editingField === 'reviewSummary_totalReviews'">
                    <div class="adjustment-input-wrapper adjustment-input-active">
                      <a-input-number
                        v-model:value="tempInputValue"
                        size="small"
                        :formatter="value => `${value >= 0 ? '+' : ''}${value}`"
                        @keydown="handleKeydown($event, 'reviewSummary_totalReviews')"
                      />
                      <CheckOutlined class="input-action-icon confirm" @click="confirmEdit('reviewSummary_totalReviews')" />
                      <CloseOutlined class="input-action-icon cancel" @click="cancelEdit" />
                    </div>
                  </template>
                  <template v-else>
                    <div class="stat-value total-count" :class="{ 'has-adjustment': hasAdjustment('reviewSummary_totalReviews') }">
                      {{ formatNumber(getAdjustedValue(reviewSummary.totalReviews, 'reviewSummary_totalReviews')) }}
                    </div>
                    <span v-if="hasAdjustment('reviewSummary_totalReviews')" class="adjustment-badge-small">
                      ({{ reviewSummary.totalReviews }}{{ adjustments.reviewSummary_totalReviews >= 0 ? '+' : '' }}{{ adjustments.reviewSummary_totalReviews }})
                    </span>
                    <EditOutlined class="edit-icon-small" @click="startEditing('reviewSummary_totalReviews', adjustments.reviewSummary_totalReviews)" />
                  </template>
                </div>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <div class="stat-label">평균 점수</div>
                <div class="stat-value average-score">{{ reviewSummary.averageScore.toFixed(2) }}</div>
              </div>
              <div class="stat-divider"></div>
              <div class="stat-item">
                <div class="stat-label">긍정 리뷰 비율(4점이상)</div>
                <div class="stat-value positive-rate">{{ formatPercentage(reviewSummary.positiveReviewRate * 100) }}</div>
              </div>
            </div>
          </a-card>
        </a-col>

        <!-- 카테고리별 리뷰 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="review-card category-card" :hoverable="true">
            <div class="card-title">📂 카테고리별 리뷰</div>
            <div class="category-list">
              <div v-for="(item, index) in reviewsByCategory" :key="index" class="category-item">
                <div class="category-info">
                  <div class="category-name">{{ item.categoryName }}</div>
                  <div class="category-percentage">{{ formatPercentage(item.percentage) }}</div>
                </div>
                <div class="category-stats">
                  <span class="category-count">{{ formatNumber(item.reviewCount) }}건</span>
                  <span class="category-score">⭐ {{ item.averageScore.toFixed(2) }}</span>
                </div>
              </div>
              <div v-if="reviewsByCategory.length === 0" class="empty-message">
                리뷰 데이터가 없습니다.
              </div>
            </div>
          </a-card>
        </a-col>

        <!-- 병원별 리뷰 카드 -->
        <a-col :xs="24" :sm="12" :lg="8">
          <a-card class="review-card hospital-card" :hoverable="true">
            <div class="card-title">🏥 병원별 리뷰 Top 10</div>
            <div class="hospital-review-list">
              <div v-for="(item, index) in reviewsByHospital" :key="index" class="hospital-review-item">
                <div class="hospital-review-info">
                  <div class="hospital-review-rank">{{ index + 1 }}</div>
                  <div class="hospital-review-name">{{ item.companyName }}</div>
                </div>
                <div class="hospital-review-stats">
                  <span class="hospital-review-count">{{ formatNumber(item.reviewCount) }}건</span>
                  <span class="hospital-review-score">⭐ {{ item.averageScore.toFixed(2) }}</span>
                </div>
              </div>
              <div v-if="reviewsByHospital.length === 0" class="empty-message">
                리뷰 데이터가 없습니다.
              </div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { Card, Row, Col, Statistic, Progress, Table, Tabs, Input, InputNumber, Button } from 'ant-design-vue';
import {
  UserOutlined,
  EditOutlined,
  CalendarOutlined,
  ShareAltOutlined,
  GiftOutlined,
  DownOutlined,
  CheckOutlined,
  CloseOutlined,
  SyncOutlined,
} from '@ant-design/icons-vue';
import { Chart, registerables } from 'chart.js';
import { formatNumber, formatPercentage, messageTypeLabels, calculateWeightedAverage } from '../utils';
import { message } from 'ant-design-vue';
import createReportApi from '../api';

Chart.register(...registerables);

export default {
  name: 'OverviewSummary',
  components: {
    ACard: Card,
    ARow: Row,
    ACol: Col,
    AStatistic: Statistic,
    AProgress: Progress,
    ATable: Table,
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    AInput: Input,
    AInputNumber: InputNumber,
    AButton: Button,
    UserOutlined,
    EditOutlined,
    CalendarOutlined,
    ShareAltOutlined,
    GiftOutlined,
    DownOutlined,
    CheckOutlined,
    CloseOutlined,
    SyncOutlined,
  },
  props: {
    dailyMessageConversions: {
      type: Array,
      default: () => []
    },
    dailyConversions: {
      type: Array,
      default: () => []
    },
    cumulativeSummaries: {
      type: Array,
      default: () => []
    },
    overallSummary: {
      type: Object,
      default: () => null
    },
    summary: {
      type: Object,
      default: () => null
    },
    reportId: {
      type: String,
      default: null
    },
    eventId: {
      type: String,
      default: null
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const { proxy } = getCurrentInstance();
    const store = useStore();

    // API 인스턴스 생성
    const api = createReportApi(proxy.$apiInstance, store);
    const trendTab = ref('certificate');
    const trendChartCertificate = ref(null);
    const trendChartFriend = ref(null);
    let chartInstances = {
      certificate: null,
      friend: null
    };

    // ==========================================
    // 병원 예약 통계 API 데이터
    // ==========================================
    const reservationApiData = ref(null);
    const reservationApiLoading = ref(false);

    // ==========================================
    // 리뷰 통계 API 데이터
    // ==========================================
    const reviewApiData = ref(null);
    const reviewApiLoading = ref(false);

    // ==========================================
    // 쿠폰 통계 API 데이터
    // ==========================================
    const couponApiData = ref(null);
    const couponApiLoading = ref(false);

    // 예약 통계 API 호출 및 EventReport에 저장
    const fetchReservationStatistics = async () => {
      if (!props.eventId) {
        message.warning('이벤트 ID가 없습니다.');
        return;
      }

      reservationApiLoading.value = true;
      try {
        // 이벤트 기간으로 필터링 (eventId는 백엔드에서 주석처리됨)
        const params = {
          eventId: props.eventId,
          medicalDept: 'OPH'  // 안과 필터
        };

        // startDate/endDate가 있으면 시간을 정리해서 추가
        // startDate: 해당 날짜의 00:00:00 UTC
        // endDate: 해당 날짜의 23:59:59 UTC
        if (props.startDate) {
          const start = new Date(props.startDate);
          start.setUTCHours(0, 0, 0, 0);
          params.startDate = start.toISOString();
        }
        if (props.endDate) {
          const end = new Date(props.endDate);
          end.setUTCHours(23, 59, 59, 999);
          params.endDate = end.toISOString();
        }

        await api.getEventReservationStatistics(
          params,
          async (data) => {
            reservationApiData.value = data;

            // reportId가 있으면 EventReport에 저장
            if (props.reportId && data) {
              try {
                await api.updateHospitalReservationStats(
                  {
                    reportId: props.reportId,
                    hospitalReservationStats: data
                  },
                  () => {
                    message.success('병원 예약 통계가 계산되고 저장되었습니다.');
                  },
                  (saveError) => {
                    console.error('예약 통계 저장 실패:', saveError);
                    message.warning('예약 통계가 계산되었지만 저장에 실패했습니다.');
                  }
                );
              } catch (saveError) {
                console.error('예약 통계 저장 중 오류:', saveError);
                message.warning('예약 통계가 계산되었지만 저장에 실패했습니다.');
              }
            } else {
              message.success('병원 예약 통계가 계산되었습니다.');
            }
          },
          (error) => {
            console.error('예약 통계 조회 실패:', error);
            message.error('예약 통계 조회에 실패했습니다.');
          }
        );
      } finally {
        reservationApiLoading.value = false;
      }
    };

    // 리뷰 통계 API 호출 및 EventReport에 저장
    const fetchReviewStatistics = async () => {
      if (!props.eventId) {
        message.warning('이벤트 ID가 없습니다.');
        return;
      }

      reviewApiLoading.value = true;
      try {
        const params = {
          eventId: props.eventId,
        };

        // startDate/endDate가 있으면 추가
        if (props.startDate) {
          const start = new Date(props.startDate);
          start.setUTCHours(0, 0, 0, 0);
          params.startDate = start.toISOString();
        }
        if (props.endDate) {
          const end = new Date(props.endDate);
          end.setUTCHours(23, 59, 59, 999);
          params.endDate = end.toISOString();
        }

        await api.getEventReviewStatistics(
          params,
          async (data) => {
            reviewApiData.value = data;

            // reportId가 있으면 EventReport에 저장
            if (props.reportId && data) {
              try {
                await api.updateReviewStats(
                  {
                    reportId: props.reportId,
                    reviewStats: data
                  },
                  () => {
                    message.success('리뷰 통계가 계산되고 저장되었습니다.');
                  },
                  (saveError) => {
                    console.error('리뷰 통계 저장 실패:', saveError);
                    message.warning('리뷰 통계가 계산되었지만 저장에 실패했습니다.');
                  }
                );
              } catch (saveError) {
                console.error('리뷰 통계 저장 중 오류:', saveError);
                message.warning('리뷰 통계가 계산되었지만 저장에 실패했습니다.');
              }
            } else {
              message.success('리뷰 통계가 계산되었습니다.');
            }
          },
          (error) => {
            console.error('리뷰 통계 조회 실패:', error);
            message.error('리뷰 통계 조회에 실패했습니다.');
          }
        );
      } finally {
        reviewApiLoading.value = false;
      }
    };

    // 쿠폰 통계 API 호출 및 EventReport에 저장
    const fetchCouponStatistics = async () => {
      if (!props.eventId) {
        message.warning('이벤트 ID가 없습니다.');
        return;
      }

      couponApiLoading.value = true;
      try {
        await api.getEventCouponStatistics(
          { eventId: props.eventId },
          async (response) => {
            // API 응답에서 실제 데이터 추출
            const data = response.data || response;
            couponApiData.value = data;

            // reportId가 있으면 EventReport에 저장
            if (props.reportId && data) {
              try {
                await api.updateCouponStats(
                  {
                    reportId: props.reportId,
                    couponStats: data
                  },
                  () => {
                    message.success('쿠폰 통계가 계산되고 저장되었습니다.');
                  },
                  (saveError) => {
                    console.error('쿠폰 통계 저장 실패:', saveError);
                    message.warning('쿠폰 통계가 계산되었지만 저장에 실패했습니다.');
                  }
                );
              } catch (saveError) {
                console.error('쿠폰 통계 저장 중 오류:', saveError);
                message.warning('쿠폰 통계가 계산되었지만 저장에 실패했습니다.');
              }
            } else {
              message.success('쿠폰 통계가 계산되었습니다.');
            }
          },
          (error) => {
            console.error('쿠폰 통계 조회 실패:', error);
            message.error('쿠폰 통계 조회에 실패했습니다.');
          }
        );
      } finally {
        couponApiLoading.value = false;
      }
    };

    // KPI 카드 지표계산 핸들러 (준비중)
    const handleParticipantsCalculate = () => {
      message.info('총 참여자 지표 계산 기능은 준비중입니다.');
    };

    const handleReviewsCalculate = () => {
      message.info('후기 작성 지표 계산 기능은 준비중입니다.');
    };

    const handleSharesCalculate = () => {
      message.info('공유 지표 계산 기능은 준비중입니다.');
    };

    // 쿠폰 발급 지표 계산 - API 연동
    const handleCouponsCalculate = () => {
      fetchCouponStatistics();
    };

    // ==========================================
    // 보정치 관리 시스템
    // ==========================================

    // 보정치 저장 상태
    const adjustments = reactive({
      // KPI 메인 수치
      totalParticipants: 0,
      totalReviews: 0,
      // 참여자 세그먼트
      participants_certificateHolder_total: 0,
      participants_certificateHolder_new: 0,
      participants_certificateHolder_existing: 0,
      participants_nonCertificate: 0,
      // 참여자 인증서 미보유자 하위 세그먼트
      participants_nonCertificate_thermage: 0,
      participants_nonCertificate_shareParticipation: 0,
      // 후기 세그먼트
      reviews_certificateHolder_total: 0,
      reviews_certificateHolder_new: 0,
      reviews_certificateHolder_existing: 0,
      // 공유 세그먼트
      totalShares: 0,
      shares_totalCustomers: 0,
      shares_certificateHolder_total: 0,
      shares_certificateHolder_customers: 0,
      shares_nonCertificate_total: 0,
      shares_nonCertificate_customers: 0,
      // 공유 인증서 보유자 하위 세그먼트 (기존/신규 인증서)
      shares_certificateHolder_existing: 0,
      shares_certificateHolder_existing_customers: 0,
      shares_certificateHolder_new: 0,
      shares_certificateHolder_new_customers: 0,
      // 공유 인증서 미보유자 하위 세그먼트 (이벤트 참여자/써마지)
      shares_nonCertificate_eventParticipant: 0,
      shares_nonCertificate_eventParticipant_customers: 0,
      shares_nonCertificate_thermage: 0,
      shares_nonCertificate_thermage_customers: 0,
      // 쿠폰 세그먼트
      totalCoupons: 0,
      coupons_totalRecipients: 0,
      coupons_certificateHolder_total: 0,
      coupons_certificateHolder_recipients: 0,
      coupons_nonCertificate_total: 0,
      coupons_nonCertificate_recipients: 0,
      // 쿠폰 인증서 보유자 하위 세그먼트 (기존/신규 인증서)
      coupons_certificateHolder_existing: 0,
      coupons_certificateHolder_existing_recipients: 0,
      coupons_certificateHolder_new: 0,
      coupons_certificateHolder_new_recipients: 0,
      // 예약 현황
      reservation_totalReservations: 0,
      reservation_uniquePatients: 0,
      reservation_totalHospitals: 0,
      // 리뷰 현황 상세
      reviewSummary_totalReviews: 0,
    });

    // 현재 편집 중인 필드
    const editingField = ref(null);
    // 임시 입력값
    const tempInputValue = ref(0);

    // 편집 시작
    const startEditing = (fieldKey, currentAdjustment) => {
      editingField.value = fieldKey;
      tempInputValue.value = currentAdjustment || 0;
      nextTick(() => {
        const input = document.querySelector('.adjustment-input-active input');
        if (input) {
          input.focus();
          input.select();
        }
      });
    };

    // 편집 완료 (저장) - API 호출
    const confirmEdit = (fieldKey) => {
      const newValue = tempInputValue.value || 0;

      // reportId가 없으면 로컬에만 저장
      if (!props.reportId) {
        adjustments[fieldKey] = newValue;
        editingField.value = null;
        tempInputValue.value = 0;
        return;
      }

      api.updateAdjustments(
        {
          reportId: props.reportId,
          adjustments: { [fieldKey]: newValue }
        },
        (response) => {
          // 성공 시 로컬 상태 업데이트
          adjustments[fieldKey] = newValue;
          editingField.value = null;
          tempInputValue.value = 0;
          message.success('보정치가 저장되었습니다.');
        },
        (error) => {
          console.error('Failed to save adjustment:', error);
          message.error('보정치 저장에 실패했습니다.');
        }
      );
    };

    // 저장된 보정치 로드
    const loadAdjustments = () => {
      if (!props.reportId) return;

      api.getAdjustments(
        { reportId: props.reportId },
        (response) => {
          // API 응답 형식: { success: true, data: { fieldKey: { value, updatedAt, updatedBy } } }
          const adjustmentsData = response?.data || response?.adjustments || {};
          if (adjustmentsData && Object.keys(adjustmentsData).length > 0) {
            // 서버에서 가져온 보정치를 로컬 상태에 반영
            Object.entries(adjustmentsData).forEach(([key, data]) => {
              if (adjustments.hasOwnProperty(key)) {
                adjustments[key] = data.value || 0;
              }
            });
          }
        },
        (error) => {
          console.error('Failed to load adjustments:', error);
        }
      );
    };

    // 편집 취소
    const cancelEdit = () => {
      editingField.value = null;
      tempInputValue.value = 0;
    };

    // 키보드 이벤트 처리
    const handleKeydown = (event, fieldKey) => {
      if (event.key === 'Enter') {
        confirmEdit(fieldKey);
      } else if (event.key === 'Escape') {
        cancelEdit();
      }
    };

    // 보정 적용된 값 계산 함수
    const getAdjustedValue = (originalValue, fieldKey) => {
      const adjustment = adjustments[fieldKey] || 0;
      return (originalValue || 0) + adjustment;
    };

    // 보정치가 있는지 확인
    const hasAdjustment = (fieldKey) => {
      return adjustments[fieldKey] !== 0 && adjustments[fieldKey] !== undefined;
    };

    // KPI 데이터 계산 (summary 우선 사용)
    const kpiData = computed(() => {
      return {
        // summary.participants.total을 사용 (전체 이벤트 참여자 수)
        totalParticipants: (props.summary && props.summary.participants && props.summary.participants.total)
          ? props.summary.participants.total
          : 0,
        // summary.reviews.total을 사용 (전체 후기 작성수)
        totalReviews: (props.summary && props.summary.reviews && props.summary.reviews.total)
          ? props.summary.reviews.total
          : 0,
        // summary.shares.total을 사용 (전체 공유수)
        totalShares: (props.summary && props.summary.shares && props.summary.shares.total)
          ? props.summary.shares.total
          : 0,
        // summary.coupons.total을 사용 (전체 쿠폰 발급량)
        totalCoupons: (props.summary && props.summary.coupons && props.summary.coupons.total)
          ? props.summary.coupons.total
          : 0,
      };
    });

    // 전체 퍼널 단계 계산
    const funnelSteps = computed(() => {
      const totalSent = props.cumulativeSummaries.reduce((sum, item) => sum + (item.totalSent || 0), 0);
      const totalClicks = props.cumulativeSummaries.reduce((sum, item) => sum + (item.totalClicks || 0), 0);
      const totalParticipants = kpiData.value.totalParticipants;
      const totalConversions = kpiData.value.totalReviews + kpiData.value.totalReservations;

      const maxValue = totalSent || 1;

      return [
        {
          label: '메시지 발송',
          value: totalSent,
          width: 100,
          color: '#1890ff',
          rate: null
        },
        {
          label: '클릭',
          value: totalClicks,
          width: (totalClicks / maxValue) * 100,
          color: '#52c41a',
          rate: totalSent > 0 ? (totalClicks / totalSent) * 100 : 0
        },
        {
          label: '참여',
          value: totalParticipants,
          width: (totalParticipants / maxValue) * 100,
          color: '#faad14',
          rate: totalClicks > 0 ? (totalParticipants / totalClicks) * 100 : 0
        },
        {
          label: '전환 (후기 + 예약)',
          value: totalConversions,
          width: (totalConversions / maxValue) * 100,
          color: '#f5222d',
          rate: totalParticipants > 0 ? (totalConversions / totalParticipants) * 100 : 0
        }
      ];
    });

    // 인증서 보유자 세그먼트 요약
    const certificateSegment = computed(() => {
      const data = props.cumulativeSummaries.filter(
        item => item.messageType === 'certificate' || item.messageType === 'review'
      );
      const sent = data.reduce((sum, item) => sum + (item.totalSent || 0), 0);
      const clicks = data.reduce((sum, item) => sum + (item.totalClicks || 0), 0);
      const reviews = data.reduce((sum, item) => sum + (item.totalReviewSubmissions || 0), 0);

      return {
        sent,
        clickRate: sent > 0 ? (clicks / sent) * 100 : 0,
        reviews,
        reviewRate: sent > 0 ? (reviews / sent) * 100 : 0,
      };
    });

    // 신규 고객 세그먼트 요약
    const nonCertificateSegment = computed(() => {
      const data = props.cumulativeSummaries.filter(item => item.messageType === 'friend');
      const quizParticipants = data.reduce((sum, item) => sum + (item.totalQuizParticipants || 0), 0);
      const reservations = data.reduce((sum, item) => sum + (item.totalReservations || 0), 0);
      const totalParticipants = kpiData.value.totalParticipants;

      return {
        quizParticipants,
        quizRate: totalParticipants > 0 ? (quizParticipants / totalParticipants) * 100 : 0,
        reservations,
        reservationRate: quizParticipants > 0 ? (reservations / quizParticipants) * 100 : 0,
      };
    });

    // 누적요약 테이블 컬럼
    const cumulativeColumns = [
      { title: '구분', dataIndex: 'category', key: 'category', width: 150, fixed: 'left' },
      { title: '발송건수', dataIndex: 'totalSent', key: 'totalSent', width: 120, align: 'right' },
      { title: '클릭률', dataIndex: 'clickRate', key: 'clickRate', width: 100, align: 'right' },
      { title: '이벤트참여자수', dataIndex: 'totalParticipants', key: 'totalParticipants', width: 140, align: 'right' },
      { title: '후기페이지유입수', dataIndex: 'totalReviewPageLandings', key: 'totalReviewPageLandings', width: 140, align: 'right' },
      { title: '만족도입력수', dataIndex: 'totalSatisfactionInputs', key: 'totalSatisfactionInputs', width: 120, align: 'right' },
      { title: '후기작성률', dataIndex: 'reviewSubmissionRate', key: 'reviewSubmissionRate', width: 120, align: 'right' },
      { title: '병원선택수', dataIndex: 'totalHospitalSelections', key: 'totalHospitalSelections', width: 110, align: 'right' },
      { title: '고객정보입력수', dataIndex: 'totalCustomerInfoInputs', key: 'totalCustomerInfoInputs', width: 130, align: 'right' },
      { title: '병원예약률', dataIndex: 'reservationRate', key: 'reservationRate', width: 120, align: 'right' },
      { title: '공유율', dataIndex: 'shareRate', key: 'shareRate', width: 100, align: 'right' },
      { title: '퀴즈참여율', dataIndex: 'quizParticipationRate', key: 'quizParticipationRate', width: 120, align: 'right' },
      { title: '퀴즈참여자유도수', dataIndex: 'totalReferredQuizParticipants', key: 'totalReferredQuizParticipants', width: 140, align: 'right' },
      { title: '쿠폰발급수', dataIndex: 'totalCouponIssuances', key: 'totalCouponIssuances', width: 110, align: 'right' },
      { title: '쿠폰발급률', dataIndex: 'couponIssuanceRate', key: 'couponIssuanceRate', width: 110, align: 'right' },
    ];

    // 누적요약 테이블 데이터 계산
    const cumulativeTableData = computed(() => {
      if (!props.cumulativeSummaries || props.cumulativeSummaries.length === 0) return [];

      const items = props.cumulativeSummaries.map(item => ({
        ...item,
        category: messageTypeLabels[item.messageType] || item.messageType,
        isTotal: false
      }));

      // 전체합계 계산
      const totalSent = items.reduce((sum, item) => sum + (item.totalSent || 0), 0);
      const totalParticipants = items.reduce((sum, item) => sum + (item.totalParticipants || 0), 0);
      const totalReviewPageLandings = items.reduce((sum, item) => sum + (item.totalReviewPageLandings || 0), 0);
      const totalSatisfactionInputs = items.reduce((sum, item) => sum + (item.totalSatisfactionInputs || 0), 0);
      const totalHospitalSelections = items.reduce((sum, item) => sum + (item.totalHospitalSelections || 0), 0);
      const totalCustomerInfoInputs = items.reduce((sum, item) => sum + (item.totalCustomerInfoInputs || 0), 0);
      const totalReferredQuizParticipants = items.reduce((sum, item) => sum + (item.totalReferredQuizParticipants || 0), 0);
      const totalCouponIssuances = items.reduce((sum, item) => sum + (item.totalCouponIssuances || 0), 0);

      items.push({
        category: '전체합계',
        totalSent,
        clickRate: calculateWeightedAverage(props.cumulativeSummaries, 'clickRate', 'totalSent'),
        totalParticipants,
        totalReviewPageLandings,
        totalSatisfactionInputs,
        totalHospitalSelections,
        totalCustomerInfoInputs,
        totalReferredQuizParticipants,
        totalCouponIssuances,
        reviewSubmissionRate: calculateWeightedAverage(props.cumulativeSummaries, 'reviewSubmissionRate', 'totalSent'),
        shareRate: calculateWeightedAverage(props.cumulativeSummaries, 'shareRate', 'totalSent'),
        quizParticipationRate: calculateWeightedAverage(props.cumulativeSummaries, 'quizParticipationRate', 'totalSent'),
        reservationRate: calculateWeightedAverage(props.cumulativeSummaries, 'reservationRate', 'totalSent'),
        couponIssuanceRate: calculateWeightedAverage(props.cumulativeSummaries, 'couponIssuanceRate', 'totalSent'),
        isTotal: true
      });

      return items;
    });

    const getCumulativeRowClassName = (record) => {
      return record.isTotal ? 'total-row' : '';
    };

    // 병원 예약 서머리 계산 (API 데이터 우선, 없으면 props 사용)
    const reservationSummary = computed(() => {
      // API 데이터가 있으면 우선 사용
      if (reservationApiData.value && reservationApiData.value.summary) {
        return {
          totalReservations: reservationApiData.value.summary.totalReservations || 0,
          uniquePatients: reservationApiData.value.summary.uniqueUsers || 0,
          totalHospitals: reservationApiData.value.summary.totalHospitals || 0,
          totalItems: 0
        };
      }
      // props 데이터 사용
      if (!props.summary || !props.summary.hospitalReservationStats || !props.summary.hospitalReservationStats.summary) {
        return {
          totalReservations: 0,
          uniquePatients: 0,
          totalHospitals: 0,
          totalItems: 0
        };
      }
      return props.summary.hospitalReservationStats.summary;
    });

    // 일자별 예약 목록 (API 데이터 우선)
    const dailyReservations = computed(() => {
      // API 데이터가 있으면 우선 사용
      if (reservationApiData.value && reservationApiData.value.dailyStats) {
        return reservationApiData.value.dailyStats.map(item => ({
          date: item.date,
          count: item.count,
          uniqueUsers: 0  // API에서는 일자별 uniqueUsers 미제공
        }));
      }
      // props 데이터 사용
      if (!props.summary || !props.summary.hospitalReservationStats || !props.summary.hospitalReservationStats.dailyReservations) {
        return [];
      }
      return props.summary.hospitalReservationStats.dailyReservations;
    });

    // 병원별 예약 목록 (API 데이터 우선)
    const hospitalReservations = computed(() => {
      // API 데이터가 있으면 우선 사용
      if (reservationApiData.value && reservationApiData.value.hospitalStats) {
        return reservationApiData.value.hospitalStats;
      }
      // props 데이터 사용
      if (!props.summary || !props.summary.hospitalReservationStats || !props.summary.hospitalReservationStats.byHospital) {
        return [];
      }
      return props.summary.hospitalReservationStats.byHospital;
    });

    // 리뷰 서머리 계산
    const reviewSummary = computed(() => {
      // 1순위: API로 조회한 실시간 데이터
      if (reviewApiData.value && reviewApiData.value.summary) {
        return reviewApiData.value.summary;
      }
      // 2순위: EventReport에 저장된 reviewStats
      if (props.summary && props.summary.reviewStats && props.summary.reviewStats.summary) {
        return props.summary.reviewStats.summary;
      }
      // 3순위: 기존 reviewAnalysisStats (하위 호환)
      if (props.summary && props.summary.reviewAnalysisStats && props.summary.reviewAnalysisStats.summary) {
        return props.summary.reviewAnalysisStats.summary;
      }
      return {
        totalReviews: 0,
        averageScore: 0,
        positiveReviewRate: 0,
        negativeReviewRate: 0,
        scoreDistribution: {
          score1: 0,
          score2: 0,
          score3: 0,
          score4: 0,
          score5: 0
        }
      };
    });

    // 카테고리별 리뷰 목록 (병합 처리)
    const reviewsByCategory = computed(() => {
      let categories = [];

      // 1순위: API로 조회한 실시간 데이터
      if (reviewApiData.value && reviewApiData.value.byCategory) {
        categories = [...reviewApiData.value.byCategory];
      }
      // 2순위: EventReport에 저장된 reviewStats
      else if (props.summary && props.summary.reviewStats && props.summary.reviewStats.byCategory) {
        categories = [...props.summary.reviewStats.byCategory];
      }
      // 3순위: 기존 reviewAnalysisStats (하위 호환)
      else if (props.summary && props.summary.reviewAnalysisStats && props.summary.reviewAnalysisStats.byCategory) {
        categories = [...props.summary.reviewAnalysisStats.byCategory];
      }

      if (categories.length === 0) {
        return [];
      }
      const mergedCategories = [];
      const processedIndices = new Set();

      // 1. "스마일", "스마일라식", "기타" 병합
      const smileIndices = [];
      categories.forEach((cat, idx) => {
        if (cat.categoryName === '스마일' || cat.categoryName === '스마일라식' || cat.categoryName === '기타') {
          smileIndices.push(idx);
        }
      });

      if (smileIndices.length > 0) {
        let totalReviews = 0;
        let weightedScore = 0;
        let totalPercentage = 0;

        smileIndices.forEach(idx => {
          const cat = categories[idx];
          totalReviews += cat.reviewCount;
          weightedScore += cat.reviewCount * cat.averageScore;
          totalPercentage += cat.percentage;
          processedIndices.add(idx);
        });

        mergedCategories.push({
          categoryName: '스마일',
          reviewCount: totalReviews,
          averageScore: totalReviews > 0 ? weightedScore / totalReviews : 0,
          percentage: totalPercentage
        });
      }

      // 2. "스마일 프로", "스마일프로" 병합
      const smileProIndices = [];
      categories.forEach((cat, idx) => {
        if (cat.categoryName === '스마일 프로' || cat.categoryName === '스마일프로') {
          smileProIndices.push(idx);
        }
      });

      if (smileProIndices.length > 0) {
        let totalReviews = 0;
        let weightedScore = 0;
        let totalPercentage = 0;

        smileProIndices.forEach(idx => {
          const cat = categories[idx];
          totalReviews += cat.reviewCount;
          weightedScore += cat.reviewCount * cat.averageScore;
          totalPercentage += cat.percentage;
          processedIndices.add(idx);
        });

        mergedCategories.push({
          categoryName: '스마일 프로',
          reviewCount: totalReviews,
          averageScore: totalReviews > 0 ? weightedScore / totalReviews : 0,
          percentage: totalPercentage
        });
      }

      // 3. 나머지 카테고리 추가
      categories.forEach((cat, idx) => {
        if (!processedIndices.has(idx)) {
          mergedCategories.push(cat);
        }
      });

      // 4. 리뷰 수 기준 정렬 후 상위 8개 반환
      return mergedCategories
        .sort((a, b) => b.reviewCount - a.reviewCount)
        .slice(0, 8);
    });

    // 병원별 리뷰 목록 (리뷰 수 기준 Top 10)
    const reviewsByHospital = computed(() => {
      // 1순위: API로 조회한 실시간 데이터 (byHospital이 직접 배열)
      if (reviewApiData.value && reviewApiData.value.byHospital) {
        return reviewApiData.value.byHospital;
      }
      // 2순위: EventReport에 저장된 reviewStats (byHospital이 직접 배열)
      if (props.summary && props.summary.reviewStats && props.summary.reviewStats.byHospital) {
        return props.summary.reviewStats.byHospital;
      }
      // 3순위: 기존 reviewAnalysisStats (하위 호환, byHospital.topByCount)
      if (props.summary && props.summary.reviewAnalysisStats && props.summary.reviewAnalysisStats.byHospital && props.summary.reviewAnalysisStats.byHospital.topByCount) {
        return props.summary.reviewAnalysisStats.byHospital.topByCount;
      }
      return [];
    });

    // 세컨더리 세그먼트 라벨 맵핑
    const secondarySegmentLabels = {
      EXISTING_CERTIFICATE: '기존 인증서',
      NEW_CERTIFICATE: '신규 인증서',
      EVENT_PARTICIPANT: '이벤트 참여자',
      NON_PARTICIPANT: '미참여자',
      QUIZ_PARTICIPANT: '퀴즈 참여자',
      REVIEW_WRITER: '후기 작성자',
      SHARER: '공유자',
      THERMAGE: '써마지',
      THERMAGE_TO_ATTENDEE: '써마지 참석자',
    };

    // 세컨더리 세그먼트 라벨 추출 (콤마로 구분된 경우 첫 번째 유효한 라벨 사용)
    const getSecondarySegmentLabel = (segmentString) => {
      if (!segmentString) return segmentString;

      // 콤마로 구분된 경우 각 부분을 순회하며 매핑된 라벨 찾기
      const segments = segmentString.split(',');
      for (const seg of segments) {
        const trimmed = seg.trim();
        if (secondarySegmentLabels[trimmed]) {
          return secondarySegmentLabels[trimmed];
        }
      }

      // 매핑된 라벨이 없으면 원본 반환
      return segmentString;
    };

    // 공유 분석 데이터 계산
    const shareAnalysisData = computed(() => {
      const analysis = props.summary?.shareSegmentAnalysis;

      if (!analysis) {
        return {
          totalShareCount: 0,
          totalShareCustomers: 0,
          overallShareRate: 0,
          hasCertificateHolder: false,
          hasNonCertificate: false,
          certificateHolder: { shareCount: 0, shareCustomers: 0, shareRate: 0, secondary: [] },
          nonCertificate: { shareCount: 0, shareCustomers: 0, shareRate: 0, secondary: [] }
        };
      }

      const processSegment = (primarySegmentData) => {
        if (!primarySegmentData) return { shareCount: 0, shareCustomers: 0, shareRate: 0, secondary: [] };

        // total 데이터 추출
        const total = primarySegmentData.total || {};

        // bySecondarySegment는 배열
        const secondary = [];
        if (primarySegmentData.bySecondarySegment && Array.isArray(primarySegmentData.bySecondarySegment)) {
          primarySegmentData.bySecondarySegment.forEach(item => {
            if (item.shareCount > 0) { // 공유 건수가 0보다 큰 경우만 표시
              secondary.push({
                label: getSecondarySegmentLabel(item.secondarySegment),
                shareCount: item.shareCount || 0,
                shareCustomers: item.shareCustomers || 0,
                shareRate: (item.shareRate || 0) * 100
              });
            }
          });
        }

        return {
          shareCount: total.shareCount || 0,
          shareCustomers: total.shareCustomers || 0,
          shareRate: (total.shareRate || 0) * 100,
          secondary
        };
      };

      const certificateHolder = processSegment(analysis.byPrimarySegment?.CERTIFICATE_HOLDER);
      const nonCertificate = processSegment(analysis.byPrimarySegment?.NON_CERTIFICATE);

      return {
        totalShareCount: analysis.overall?.shareCount || 0,
        totalShareCustomers: analysis.overall?.shareCustomers || 0,
        overallShareRate: (analysis.overall?.shareRate || 0) * 100,
        hasCertificateHolder: certificateHolder.shareCount > 0,
        hasNonCertificate: nonCertificate.shareCount > 0,
        certificateHolder,
        nonCertificate
      };
    });

    // 쿠폰 분석 데이터 계산
    const couponAnalysisData = computed(() => {
      // 1순위: API로 조회한 실시간 쿠폰 통계
      if (couponApiData.value && couponApiData.value.summary) {
        const apiSummary = couponApiData.value.summary;
        const bySegment = couponApiData.value.bySegment || {};
        const certHolder = bySegment.certificateHolder || { couponIssuances: 0, couponRecipients: 0, secondary: [] };
        const nonCert = bySegment.nonCertificate || { couponIssuances: 0, couponRecipients: 0, secondary: [] };

        return {
          // API 데이터 매핑: totalCouponSent → totalCouponIssuances, couponIssued → totalCouponRecipients
          totalCouponIssuances: apiSummary.totalCouponSent || 0,
          totalCouponRecipients: apiSummary.couponIssued || 0,
          // 세그먼트 분석 데이터
          hasCertificateHolder: certHolder.couponIssuances > 0,
          hasNonCertificate: nonCert.couponIssuances > 0,
          certificateHolder: certHolder,
          nonCertificate: nonCert,
          // 추가 API 데이터 (UI 확장 시 사용 가능)
          apiStats: {
            winnerSelected: apiSummary.winnerSelected || 0,
            couponUsed: apiSummary.couponUsed || 0,
            issuedRate: apiSummary.issuedRate || 0,
            usedRate: apiSummary.usedRate || 0
          },
          byStatus: couponApiData.value.byStatus || [],
          dailyCoupons: couponApiData.value.dailyCoupons || []
        };
      }

      // 2순위: EventReport에 저장된 couponStats
      if (props.summary?.couponStats?.summary) {
        const savedSummary = props.summary.couponStats.summary;
        const bySegment = props.summary.couponStats.bySegment || {};
        const certHolder = bySegment.certificateHolder || { couponIssuances: 0, couponRecipients: 0, secondary: [] };
        const nonCert = bySegment.nonCertificate || { couponIssuances: 0, couponRecipients: 0, secondary: [] };

        return {
          totalCouponIssuances: savedSummary.totalCouponSent || 0,
          totalCouponRecipients: savedSummary.couponIssued || 0,
          hasCertificateHolder: certHolder.couponIssuances > 0,
          hasNonCertificate: nonCert.couponIssuances > 0,
          certificateHolder: certHolder,
          nonCertificate: nonCert,
          apiStats: {
            winnerSelected: savedSummary.winnerSelected || 0,
            couponUsed: savedSummary.couponUsed || 0,
            issuedRate: savedSummary.issuedRate || 0,
            usedRate: savedSummary.usedRate || 0
          },
          byStatus: props.summary.couponStats.byStatus || [],
          dailyCoupons: props.summary.couponStats.dailyCoupons || []
        };
      }

      // 3순위: 기존 couponSegmentAnalysis (세그먼트별 분석)
      const analysis = props.summary?.couponSegmentAnalysis;

      if (!analysis) {
        return {
          totalCouponIssuances: 0,
          totalCouponRecipients: 0,
          hasCertificateHolder: false,
          hasNonCertificate: false,
          certificateHolder: { couponIssuances: 0, couponRecipients: 0, secondary: [] },
          nonCertificate: { couponIssuances: 0, couponRecipients: 0, secondary: [] }
        };
      }

      const processCouponSegment = (primarySegmentData) => {
        if (!primarySegmentData) return { couponIssuances: 0, couponRecipients: 0, secondary: [] };

        // total 데이터 추출
        const total = primarySegmentData.total || {};

        // bySecondarySegment는 배열
        const secondary = [];
        if (primarySegmentData.bySecondarySegment && Array.isArray(primarySegmentData.bySecondarySegment)) {
          primarySegmentData.bySecondarySegment.forEach(item => {
            if (item.couponIssuances > 0) { // 쿠폰 발급 건수가 0보다 큰 경우만 표시
              secondary.push({
                label: getSecondarySegmentLabel(item.secondarySegment),
                couponIssuances: item.couponIssuances || 0,
                couponRecipients: item.couponRecipients || 0
              });
            }
          });
        }

        return {
          couponIssuances: total.couponIssuances || 0,
          couponRecipients: total.couponRecipients || 0,
          secondary
        };
      };

      const certificateHolder = processCouponSegment(analysis.byPrimarySegment?.CERTIFICATE_HOLDER);
      const nonCertificate = processCouponSegment(analysis.byPrimarySegment?.NON_CERTIFICATE);

      return {
        totalCouponIssuances: analysis.overall?.couponIssuances || 0,
        totalCouponRecipients: analysis.overall?.couponRecipients || 0,
        hasCertificateHolder: certificateHolder.couponIssuances > 0,
        hasNonCertificate: nonCertificate.couponIssuances > 0,
        certificateHolder,
        nonCertificate
      };
    });

    // 날짜 포맷팅 함수
    const formatDate = (dateInput) => {
      if (!dateInput) return '';

      // dateInput이 문자열이면 그대로 사용, 객체이면 date 속성 추출
      let dateString = dateInput;
      if (typeof dateInput === 'object' && dateInput.date) {
        dateString = dateInput.date;
      }

      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${month}/${day}`;
    };

    // ZEISS 컬러 팔레트
    const COLORS = {
      zeissBlue: '#005EB8',
      certificate: '#005EB8',
      friend: '#008C73',
      review: '#8A5FFB',
      success: '#52c41a',
      warning: '#faad14',
      error: '#f5222d',
      info: '#1890ff',
    };

    // Certificate 차트 생성
    const createCertificateChart = () => {
      if (!trendChartCertificate.value) return;

      if (chartInstances.certificate) {
        try {
          chartInstances.certificate.destroy();
        } catch (e) {
          console.warn('Failed to destroy certificate chart:', e);
        }
        chartInstances.certificate = null;
      }

      const sortedData = [...props.dailyMessageConversions]
        .filter(item => item.messageType === 'certificate')
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // 데이터가 없으면 차트를 생성하지 않음
      if (sortedData.length === 0) return;

      const labels = sortedData.map(item => {
        const date = new Date(item.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      });

      const ctx = trendChartCertificate.value.getContext('2d');
      chartInstances.certificate = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: '발송',
              data: sortedData.map(d => d.sent || 0),
              backgroundColor: COLORS.zeissBlue,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '클릭',
              data: sortedData.map(d => d.clicks || 0),
              backgroundColor: COLORS.info,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '후기작성',
              data: sortedData.map(d => d.reviewSubmissions || 0),
              backgroundColor: COLORS.success,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '공유',
              data: sortedData.map(d => d.shares || 0),
              backgroundColor: COLORS.warning,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '클릭률 (%)',
              data: sortedData.map(d => d.clickRate || 0),
              type: 'line',
              borderColor: COLORS.info,
              backgroundColor: 'transparent',
              borderWidth: 2,
              yAxisID: 'y1',
              pointBackgroundColor: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return COLORS.info;
                const value = context.parsed.y;
                return value > 100 ? COLORS.error : COLORS.info;
              },
              pointRadius: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return 4;
                const value = context.parsed.y;
                return value > 100 ? 6 : 4;
              },
              pointBorderWidth: 2,
              pointBorderColor: '#fff'
            },
            {
              label: '후기작성률 (%)',
              data: sortedData.map(d => d.reviewSubmissionRate || 0),
              type: 'line',
              borderColor: COLORS.success,
              backgroundColor: 'transparent',
              borderWidth: 2,
              yAxisID: 'y1',
              pointBackgroundColor: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return COLORS.success;
                const value = context.parsed.y;
                return value > 100 ? COLORS.error : COLORS.success;
              },
              pointRadius: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return 4;
                const value = context.parsed.y;
                return value > 100 ? 6 : 4;
              },
              pointBorderWidth: 2,
              pointBorderColor: '#fff'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  if (context.datasetIndex >= 4) {
                    return `${context.dataset.label}: ${value.toFixed(2)}%`;
                  }
                  return `${context.dataset.label}: ${formatNumber(value)}`;
                },
                afterLabel: function(context) {
                  if (context.datasetIndex >= 4 && context.parsed.y > 100) {
                    return '⚠️ 분모작음';
                  }
                  return '';
                }
              }
            }
          },
          scales: {
            y: {
              type: 'linear',
              position: 'left',
              title: { display: true, text: '건수' },
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return formatNumber(value);
                }
              }
            },
            y1: {
              type: 'linear',
              position: 'right',
              title: { display: true, text: '비율 (%)' },
              grid: { drawOnChartArea: false },
              beginAtZero: true
            }
          }
        }
      });
    };

    // Friend 차트 생성
    const createFriendChart = () => {
      if (!trendChartFriend.value) return;

      if (chartInstances.friend) {
        try {
          chartInstances.friend.destroy();
        } catch (e) {
          console.warn('Failed to destroy friend chart:', e);
        }
        chartInstances.friend = null;
      }

      const sortedData = [...props.dailyMessageConversions]
        .filter(item => item.messageType === 'friend')
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // 데이터가 없으면 차트를 생성하지 않음
      if (sortedData.length === 0) return;

      const labels = sortedData.map(item => {
        const date = new Date(item.date);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      });

      const ctx = trendChartFriend.value.getContext('2d');
      chartInstances.friend = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label: '퀴즈 참여',
              data: sortedData.map(d => d.quizParticipants || 0),
              backgroundColor: COLORS.friend,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '예약',
              data: sortedData.map(d => d.reservations || 0),
              backgroundColor: COLORS.error,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '공유',
              data: sortedData.map(d => d.shares || 0),
              backgroundColor: COLORS.warning,
              borderRadius: 6,
              yAxisID: 'y'
            },
            {
              label: '퀴즈→예약율 (%)',
              data: sortedData.map(d => {
                if (d.quizParticipants > 0) {
                  return ((d.reservations || 0) / d.quizParticipants * 100);
                }
                return 0;
              }),
              type: 'line',
              borderColor: COLORS.error,
              backgroundColor: 'transparent',
              borderWidth: 2,
              yAxisID: 'y1',
              pointBackgroundColor: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return COLORS.friend;
                const value = context.parsed.y;
                return value > 100 ? COLORS.error : COLORS.friend;
              },
              pointRadius: function(context) {
                if (!context.parsed || context.parsed.y === undefined) return 4;
                const value = context.parsed.y;
                return value > 100 ? 6 : 4;
              },
              pointBorderWidth: 2,
              pointBorderColor: '#fff'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: {
              position: 'top',
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const value = context.parsed.y;
                  if (context.datasetIndex >= 3) {
                    return `${context.dataset.label}: ${value.toFixed(2)}%`;
                  }
                  return `${context.dataset.label}: ${formatNumber(value)}`;
                },
                afterLabel: function(context) {
                  if (context.datasetIndex >= 3 && context.parsed.y > 100) {
                    return '⚠️ 분모작음';
                  }
                  return '';
                }
              }
            }
          },
          scales: {
            y: {
              type: 'linear',
              position: 'left',
              title: { display: true, text: '건수' },
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return formatNumber(value);
                }
              }
            },
            y1: {
              type: 'linear',
              position: 'right',
              title: { display: true, text: '비율 (%)' },
              grid: { drawOnChartArea: false },
              beginAtZero: true
            }
          }
        }
      });
    };

    // 모든 차트 정리
    const destroyAllCharts = () => {
      if (chartInstances.certificate) {
        try {
          chartInstances.certificate.destroy();
        } catch (e) {
          console.warn('Failed to destroy certificate chart:', e);
        }
        chartInstances.certificate = null;
      }
      if (chartInstances.friend) {
        try {
          chartInstances.friend.destroy();
        } catch (e) {
          console.warn('Failed to destroy friend chart:', e);
        }
        chartInstances.friend = null;
      }
    };

    // 모든 차트 생성
    const createAllCharts = () => {
      nextTick(() => {
        createCertificateChart();
        createFriendChart();
      });
    };

    // 데이터 변경 시 차트 업데이트
    watch(() => props.dailyMessageConversions, () => {
      createAllCharts();
    }, { deep: true });

    // 탭 변경 감지 (필요 시 차트 재생성)
    watch(trendTab, (newTab) => {
      nextTick(() => {
        if (newTab === 'certificate') createCertificateChart();
        else if (newTab === 'friend') createFriendChart();
      });
    });

    onMounted(() => {
      createAllCharts();
      // 보정치 로드
      loadAdjustments();
    });

    // reportId 변경 시 보정치 다시 로드
    watch(() => props.reportId, (newReportId) => {
      if (newReportId) {
        // 기존 보정치 초기화
        Object.keys(adjustments).forEach(key => {
          adjustments[key] = 0;
        });
        // 새 보정치 로드
        loadAdjustments();
      }
    });

    onBeforeUnmount(() => {
      destroyAllCharts();
    });

    return {
      trendTab,
      trendChartCertificate,
      trendChartFriend,
      kpiData,
      funnelSteps,
      certificateSegment,
      nonCertificateSegment,
      cumulativeColumns,
      cumulativeTableData,
      getCumulativeRowClassName,
      reservationSummary,
      dailyReservations,
      hospitalReservations,
      reservationApiData,
      reservationApiLoading,
      fetchReservationStatistics,
      reviewApiData,
      reviewApiLoading,
      fetchReviewStatistics,
      couponApiData,
      couponApiLoading,
      fetchCouponStatistics,
      reviewSummary,
      reviewsByCategory,
      reviewsByHospital,
      shareAnalysisData,
      couponAnalysisData,
      // KPI 카드 지표계산 핸들러
      handleParticipantsCalculate,
      handleReviewsCalculate,
      handleSharesCalculate,
      handleCouponsCalculate,
      formatNumber,
      formatPercentage,
      formatDate,
      // 보정치 관리 시스템
      adjustments,
      editingField,
      tempInputValue,
      startEditing,
      confirmEdit,
      cancelEdit,
      handleKeydown,
      getAdjustedValue,
      hasAdjustment,
    };
  },
};
</script>

<style scoped>
.overview-summary {
  padding: 24px;
  background: #fafafa;
}

/* KPI 카드 섹션 */
.kpi-section {
  margin-bottom: 24px;
}

.kpi-card {
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
  height: 100%;
  min-height: 150px;
  position: relative;
  overflow: hidden;
}

.kpi-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 94, 184, 0.03) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

:deep(.kpi-card .ant-card-body) {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  z-index: 1;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.12);
  border-color: #005EB8;
}

.kpi-card:hover::before {
  opacity: 1;
}

.kpi-subtitle {
  margin-top: 8px;
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 500;
}

.kpi-rate {
  font-size: 15px;
  color: #595959;
  margin-left: 6px;
  font-weight: 500;
}

/* ==========================================
   보정치 편집 UI 스타일
   ========================================== */

/* KPI 메인 값 컨테이너 */
.kpi-main-value {
  margin-bottom: 4px;
}

/* KPI 타이틀과 버튼 래퍼 */
.kpi-title-with-button {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.kpi-title-with-button .kpi-title {
  margin-bottom: 0;
}

.kpi-calculate-btn {
  font-size: 11px;
  padding: 2px 8px;
  height: 22px;
}

.kpi-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 8px;
}

.kpi-title .kpi-icon {
  font-size: 16px;
  color: #1890ff;
}

.kpi-title-reviews .kpi-icon {
  color: #52c41a;
}

/* 편집 가능한 값 컨테이너 */
.editable-value-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* 메인 값 스타일 */
.main-value {
  font-size: 28px;
  font-weight: 700;
  color: #1890ff;
}

.main-value-reviews {
  color: #52c41a;
}

.main-value-shares {
  color: #faad14;
}

.main-value-coupons {
  color: #722ed1;
}

.kpi-title-shares .kpi-icon {
  color: #faad14;
}

.kpi-title-coupons .kpi-icon {
  color: #722ed1;
}

.main-value.has-adjustment {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 4px;
}

/* 연필 아이콘 (메인 KPI) */
.edit-icon {
  font-size: 14px;
  color: #bfbfbf;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.edit-icon:hover {
  color: #1890ff;
  background: #e6f7ff;
}

/* 연필 아이콘 (세그먼트 레벨) */
.edit-icon-small {
  font-size: 12px;
  color: #bfbfbf;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 3px;
  margin-left: 4px;
}

.edit-icon-small:hover {
  color: #1890ff;
  background: #e6f7ff;
}

/* 연필 아이콘 (breakdown item 레벨) */
.edit-icon-tiny {
  font-size: 10px;
  color: #d9d9d9;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  border-radius: 2px;
  margin-left: 2px;
}

.edit-icon-tiny:hover {
  color: #1890ff;
  background: #e6f7ff;
}

/* 보정치 배지 (메인 KPI) */
.adjustment-badge {
  font-size: 12px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 500;
}

/* 보정치 배지 (세그먼트 레벨) */
.adjustment-badge-small {
  font-size: 10px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 1px 4px;
  border-radius: 3px;
  font-weight: 500;
  margin-left: 4px;
}

/* 보정치 배지 (breakdown item 레벨) */
.adjustment-badge-tiny {
  font-size: 9px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 1px 3px;
  border-radius: 2px;
  font-weight: 500;
  margin-left: 2px;
}

/* 인풋 래퍼 */
.adjustment-input-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fafafa;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #d9d9d9;
}

.adjustment-input-wrapper.inline {
  padding: 2px 4px;
  border-radius: 4px;
}

.adjustment-input-wrapper.inline-input {
  display: inline-flex;
  padding: 2px 4px;
  border-radius: 4px;
  vertical-align: middle;
}

.adjustment-input-wrapper :deep(.ant-input-number) {
  width: 80px;
}

.adjustment-input-wrapper.inline :deep(.ant-input-number) {
  width: 60px;
}

.adjustment-input-wrapper.inline-input :deep(.ant-input-number) {
  width: 60px;
}

.adjustment-input-wrapper :deep(.ant-input-number-sm) {
  font-size: 12px;
}

/* 인풋 액션 아이콘 */
.input-action-icon {
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.input-action-icon.confirm {
  color: #52c41a;
}

.input-action-icon.confirm:hover {
  background: #f6ffed;
}

.input-action-icon.cancel {
  color: #ff4d4f;
}

.input-action-icon.cancel:hover {
  background: #fff2f0;
}

/* 보정치가 있는 값 표시 */
.breakdown-value.has-adjustment,
.item-value.has-adjustment {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
}

/* 총 참여자 카드 전용 스타일 */
.kpi-card-participants {
  min-height: 320px;
}

.participants-breakdown {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

/* 후기 작성 카드 전용 스타일 */
.kpi-card-reviews {
  min-height: 320px;
}

.reviews-breakdown {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

/* 공유 카드 전용 스타일 */
.kpi-card-shares {
  min-height: 320px;
}

.shares-breakdown {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

/* 쿠폰 카드 전용 스타일 */
.kpi-card-coupons {
  min-height: 320px;
}

.coupons-breakdown {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.breakdown-section {
  margin-bottom: 12px;
  padding: 8px 0;
}

.breakdown-section:last-child {
  margin-bottom: 0;
}

.breakdown-title {
  font-size: 12px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 4px;
}

.breakdown-value-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.breakdown-value {
  font-size: 16px;
  font-weight: 700;
  color: #1890ff;
}

.breakdown-inline-desc {
  font-size: 11px;
  color: #a0a0a0;
  font-style: italic;
  line-height: 1.3;
}

.breakdown-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.breakdown-item {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #8c8c8c;
  margin-bottom: 4px;
}

.item-label {
  font-weight: 500;
  flex-shrink: 0;
}

.item-label strong {
  font-weight: 700;
}

.item-value {
  font-weight: 600;
  color: #595959;
  flex-shrink: 0;
}

.item-inline-desc {
  font-size: 10px;
  color: #a0a0a0;
  font-style: italic;
  line-height: 1.3;
}

.breakdown-desc {
  font-size: 11px;
  color: #8c8c8c;
  font-style: italic;
  margin-top: 4px;
}

:deep(.kpi-card .ant-statistic-title) {
  font-size: 14px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 12px;
}

:deep(.kpi-card .ant-statistic-content-prefix) {
  font-size: 20px;
  margin-right: 12px;
}

/* 퍼널 + 세그먼트 섹션 */
.funnel-segment-section {
  margin-bottom: 24px;
}

/* 콤팩트 퍼널 차트 */
.funnel-card-compact {
  height: 100%;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.funnel-container-compact {
  padding: 16px 12px;
}

.funnel-step-compact {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  margin-bottom: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.funnel-step-compact::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 94, 184, 0.05) 0%, rgba(0, 94, 184, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.funnel-step-compact:hover {
  transform: translateY(-4px);
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.15);
}

.funnel-step-compact:hover::before {
  opacity: 1;
}

.step-header-compact {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
}

.step-number-compact {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 12px;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.funnel-step-compact:hover .step-number-compact {
  transform: scale(1.1);
}

.step-label-compact {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  flex: 1;
  transition: color 0.3s ease;
}

.funnel-step-compact:hover .step-label-compact {
  color: #005EB8;
}

.step-metrics-compact {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
  padding: 0 4px;
  position: relative;
  z-index: 1;
}

.step-value-compact {
  font-size: 18px;
  font-weight: 700;
  color: #262626;
}

.step-rate-compact {
  font-size: 12px;
  font-weight: 600;
  color: #1890ff;
  background: #e6f7ff;
  padding: 2px 8px;
  border-radius: 4px;
}

:deep(.funnel-step-compact .ant-progress) {
  position: relative;
  z-index: 1;
}

.funnel-arrow-compact {
  text-align: center;
  margin: 6px 0;
  color: #bfbfbf;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.funnel-step-compact:hover + .funnel-arrow-compact {
  color: #005EB8;
  transform: translateY(2px);
}

.segment-card {
  height: 100%;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.segment-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 94, 184, 0.02) 0%, transparent 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.segment-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 94, 184, 0.1);
  border-color: #005EB8;
}

.segment-card:hover::before {
  opacity: 1;
}

:deep(.segment-card .ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
}

:deep(.segment-card .ant-card-body) {
  padding: 20px;
}

.segment-stats {
  margin-bottom: 16px;
}

.segment-goal {
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  font-size: 13px;
  color: #8c8c8c;
  font-weight: 500;
}

/* 트렌드 섹션 */
.trend-section {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.trend-section .ant-card-body) {
  padding: 24px;
}

.trend-tabs {
  margin-top: 16px;
}

:deep(.trend-tabs .ant-tabs-tab) {
  font-weight: 500;
  font-size: 14px;
}

:deep(.trend-tabs .ant-tabs-tab-active) {
  font-weight: 600;
}

:deep(.trend-tabs .ant-tabs-content) {
  padding-top: 16px;
}

/* 누적요약 섹션 */
.cumulative-section {
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.cumulative-section .ant-card-body) {
  padding: 24px;
}

.total-row-text {
  font-weight: 700;
  color: #1890ff;
  font-size: 14px;
}

:deep(.total-row) {
  background: linear-gradient(to right, #f0f5ff, #f5f5f5);
  font-weight: 600;
}

:deep(.total-row td) {
  border-top: 2px solid #1890ff !important;
}

:deep(.ant-card-head-title) {
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

:deep(.ant-card-head) {
  background: linear-gradient(to bottom, #fafafa, #fff);
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 24px;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

/* 병원 예약 섹션 */
.reservation-section {
  margin: 24px 0;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e8e8e8;
}

.section-header-row .section-header {
  margin: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.section-header {
  font-size: 18px;
  font-weight: 700;
  color: #262626;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #e8e8e8;
}

.reservation-card {
  height: 100%;
  min-height: 450px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.reservation-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.12);
  border-color: #005EB8;
}

:deep(.reservation-card .ant-card-body) {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

/* 서머리 카드 */
.summary-stats {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
}

.stat-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.stat-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  transform: scale(1.02);
}

.stat-label {
  font-size: 13px;
  color: #8c8c8c;
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
}

.stat-value.total-count {
  color: #1890ff;
}

.stat-value.unique-count {
  color: #52c41a;
}

.stat-value.hospital-count {
  color: #722ed1;
}

/* 편집 가능한 stat 값 컨테이너 */
.editable-stat-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.editable-stat-value .stat-value {
  display: inline-flex;
  align-items: center;
}

.editable-stat-value .stat-value.has-adjustment {
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 4px;
}

.editable-stat-value .edit-icon-small {
  margin-left: 6px;
}

.editable-stat-value .adjustment-badge-small {
  margin-top: 4px;
}

.stat-divider {
  height: 1px;
  background: #e8e8e8;
}

/* 일자별 예약 카드 */
.daily-list {
  flex: 1;
}

.daily-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.daily-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  transform: translateX(4px);
}

.daily-date {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
}

.daily-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.daily-count {
  font-size: 14px;
  font-weight: 700;
  color: #1890ff;
}

.daily-users {
  font-size: 12px;
  color: #8c8c8c;
}

/* 병원별 예약 카드 */
.hospital-list {
  flex: 1;
}

.hospital-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.hospital-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  transform: translateX(4px);
}

.hospital-name {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  flex: 1;
}

.hospital-count {
  font-size: 14px;
  font-weight: 700;
  color: #52c41a;
  padding: 2px 10px;
  background: #e9fbef;
  border-radius: 4px;
}

/* 빈 메시지 */
.empty-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 20px;
  color: #8c8c8c;
  font-size: 13px;
}

/* 리뷰 현황 섹션 */
.review-section {
  margin: 24px 0;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.review-card {
  height: 100%;
  min-height: 450px;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.review-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 94, 184, 0.12);
  border-color: #005EB8;
}

:deep(.review-card .ant-card-body) {
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 리뷰 서머리 카드 - 병원 예약 카드와 동일한 스타일 사용 */

/* 카테고리별 리뷰 카드 */
.category-list {
  flex: 1;
  max-height: 380px;
  overflow-y: auto;
}

.category-item {
  padding: 10px 12px;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.category-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  transform: translateX(4px);
}

.category-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.category-name {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  flex: 1;
}

.category-percentage {
  font-size: 11px;
  color: #8c8c8c;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.category-stats {
  display: flex;
  align-items: center;
  gap: 12px;
}

.category-count {
  font-size: 14px;
  font-weight: 700;
  color: #1890ff;
}

.category-score {
  font-size: 12px;
  font-weight: 600;
  color: #faad14;
}

/* 병원별 리뷰 카드 */
.hospital-review-list {
  flex: 1;
  max-height: 380px;
  overflow-y: auto;
}

.hospital-review-item {
  padding: 10px 12px;
  margin-bottom: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  transition: all 0.3s ease;
}

.hospital-review-item:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%);
  border-color: #005EB8;
  transform: translateX(4px);
}

.hospital-review-info {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}

.hospital-review-rank {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1890ff, #40a9ff);
  color: white;
  font-size: 11px;
  font-weight: 700;
  border-radius: 50%;
  flex-shrink: 0;
}

.hospital-review-name {
  font-size: 13px;
  font-weight: 600;
  color: #262626;
  flex: 1;
}

.hospital-review-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
}

.hospital-review-count {
  font-size: 14px;
  font-weight: 700;
  color: #52c41a;
}

.hospital-review-score {
  font-size: 12px;
  font-weight: 600;
  color: #faad14;
  padding: 2px 8px;
  background: #fff7e6;
  border-radius: 4px;
}

/* 평균 점수 stat-value 스타일 */
.stat-value.average-score {
  color: #faad14;
}

/* 긍정 리뷰 비율 stat-value 스타일 */
.stat-value.positive-rate {
  color: #52c41a;
}
</style>
